<template>
  <div class="vendor_create_bill-main glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Vendor', 'Create Bill']" />

    <CreateBillProgressor
      :show="createBillProgressorShow"
      :steps="createBillProgressorSteps"
      :ok-enabled="createBillProgressorOkEnabled"
      :foot-message="createBillProgressorFootMessage"
      :foot-tone="createBillProgressorFootTone"
      @ok="onCreateBillProgressorOk"
    />

    <!-- Bill journal entry is created in the background (no review modal) -->
    <div
      v-if="journalAutoCreateActive"
      class="vendor-create-bill-journal-auto-create-root"
      aria-hidden="true"
    >
      <BillJournalEntry
        :bill-data="billDataForJournal"
        :is-modal="true"
        :auto-populate="true"
        :auto-create="true"
        @saved="handleJournalEntrySaved"
        @auto-create-skipped="handleJournalEntryAutoSkipped"
        @auto-create-failed="handleJournalEntryAutoFailed"
        @cancelled="closeJournalEntryModal"
      />
    </div>

    <form class="vendor_create_bill-form">
      <div
        v-if="capturedReviewHint"
        class="vendor-create-bill-captured-hint"
      >
        {{ capturedReviewHint }}
      </div>
      <div class="vendor_create_bill-date-field-wrapper">
        <div class="vendor_create_bill-date-no-group">
          <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-date vendor_create_bill-meta-field vendor_create_bill-meta-field--date">
            <LabelDateField
              input-id="create-bill-date"
              label="Bill Date"
              v-model="billDate"
              placeholder="MM/DD/YYYY"
              flex
            />
          </div>
          <div
            class="vendor_create_bill-form-group vendor_create_bill-form-group--label-input vendor_create_bill-meta-field vendor_create_bill-meta-field--bill-no vendor_create_bill-bill-no-wrap"
            :style="billNoFieldStyle"
          >
            <LabelInputField
              input-id="create-bill-no"
              label="Bill No"
              v-model="invNo"
              placeholder="Bill No"
              custom-class="vendor_create_bill-bill-no-field"
            />
          </div>
        </div>
      </div>
      <div class="vendor_create_bill-form-row">
        <div class="vendor_create_bill-form-col">
          <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-dropdown vendor_create_bill-required">
            <LabelDropdown
              label="Vendor Name"
              select-id="create-bill-vendor"
              v-model="vendorId"
              flex
              wide
              required
              searchable
              menu-class="vendor-create-bill-dropdown-menu"
              :options="vendorDropdownOptions"
            />
          </div>
          <div v-if="$route.query.vendorName && vendorId" class="vendor-create-bill-prefill-info vendor_create_bill-glass-hint">
            <i class="bi bi-check-circle" aria-hidden="true"></i>
            <small class="vendor_create_bill-prefill-selected">
              Pre-selected: {{ $route.query.vendorName }}
            </small>
          </div>
          <div v-if="$route.query.filterByConsultant === 'true' && $route.query.consultantName" class="vendor-create-bill-prefill-info vendor_create_bill-glass-hint">
            <i class="bi bi-person-check" aria-hidden="true"></i>
            <small class="vendor_create_bill-prefill-selected">
              Consultant: {{ $route.query.consultantName }}
            </small>
          </div>
          <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-dropdown">
            <LabelDropdown
              label="Contact Name"
              select-id="create-bill-contact"
              v-model="contactName"
              flex
              wide
              searchable
              menu-class="vendor-create-bill-dropdown-menu"
              :options="contactDropdownOptions"
            />
          </div>
        </div>
        <div class="vendor_create_bill-form-col">
          <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-input">
            <LabelInputField
              input-id="create-bill-phone"
              label="Phone"
              v-model="phone"
              placeholder="Phone"
              flex
            />
          </div>
          <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-dropdown">
            <LabelDropdown
              label="Payment Terms"
              select-id="create-bill-payment-term"
              v-model="paymentTerm"
              flex
              wide
              searchable
              menu-class="vendor-create-bill-dropdown-menu"
              :options="paymentTermDropdownOptions"
              @change="handlePaymentTermChange"
            />
          </div>
        </div>
        <div class="vendor_create_bill-form-col">
          <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-date">
            <LabelDateField
              input-id="create-bill-due-date"
              label="Due Date"
              v-model="dueDate"
              placeholder="MM/DD/YYYY"
              flex
            />
          </div>
        </div>
      </div>
      <!-- Itemize tabs -->
      <div class="vendor_create_bill-itemize-wrap">
        <div class="vendor_create_bill-tabs" role="tablist" aria-label="Bill itemize method">
          <button
            v-if="isConsultantIndustry"
            type="button"
            role="tab"
            class="vendor_create_bill-tab"
            :class="{ 'vendor_create_bill-tab-active': activeTab === 'consultant' }"
            :aria-selected="activeTab === 'consultant' ? 'true' : 'false'"
            @click="setActiveTab('consultant')"
          >
            <i class="bi bi-person-workspace" aria-hidden="true"></i>
            Itemize by Consultant Hours
          </button>
          <button
            type="button"
            role="tab"
            class="vendor_create_bill-tab"
            :class="{ 'vendor_create_bill-tab-active': activeTab === 'account' }"
            :aria-selected="activeTab === 'account' ? 'true' : 'false'"
            @click="setActiveTab('account')"
          >
            <i class="bi bi-journal-text" aria-hidden="true"></i>
            Itemize by Account
          </button>
          <button
            type="button"
            role="tab"
            class="vendor_create_bill-tab"
            :class="{ 'vendor_create_bill-tab-active': activeTab === 'product' }"
            :aria-selected="activeTab === 'product' ? 'true' : 'false'"
            @click="setActiveTab('product')"
          >
            <i class="bi bi-box-seam" aria-hidden="true"></i>
            Itemize by Product/Service
          </button>
        </div>

        <!-- Itemize by Account -->
        <div
          v-show="activeTab === 'account'"
          role="tabpanel"
          class="vendor_create_bill-itemize-section vendor_create_bill-tab-panel glass-tab-panel--nested"
          :class="{ 'vendor_create_bill-tab-panel--enter': tabPanelEnter === 'account' }"
          @animationend="onTabPanelAnimEnd($event, 'account')"
        >
          <div class="consultant-glass-table-section vendor_create_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
          <table class="vendor_create_bill-itemize-table consultant-glass-table">
            <thead>
              <tr>
                <th class="vendor_create_bill-th-account">
                  <span class="vendor_create_bill-th-account-label">Account</span>
                  <div ref="accountInfoTrigger" class="vendor_create_bill-account-info-trigger">
                    <button
                      type="button"
                      class="vendor_create_bill-account-info-btn"
                      :class="{ 'vendor_create_bill-account-info-btn--active': accountInfoOpen }"
                      :aria-expanded="accountInfoOpen ? 'true' : 'false'"
                      aria-label="Show account itemization help"
                      @click.stop="toggleAccountInfo"
                    >
                      <i class="bi bi-info-circle" aria-hidden="true"></i>
                    </button>
                  </div>
                </th>
                <th>Description</th>
                <th>Amount(USD)</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <!-- Auto-populated rows from consultants/product-service items -->
              <tr v-for="(row, idx) in visibleAccountRows" :key="'auto-account-' + idx">
                <td class="vendor_create_bill-table-cell--field">
                  <LabelDropdown
                    label="Account"
                    :select-id="`create-bill-auto-account-${idx}`"
                    v-model="row.accountId"
                    inline
                    wide
                    searchable
                    disabled
                    custom-class="vendor_create_bill-table-field"
                    menu-class="vendor-create-bill-dropdown-menu"
                    :options="autoAccountDropdownOptions"
                  />
                </td>
                <td>
                  <div class="vendor_create_bill-item-description">
                    <div class="vendor_create_bill-item-name">
                      <i v-if="row.itemType === 'consultant'" class="bi bi-person-workspace vendor_create_bill-item-type-icon" aria-hidden="true"></i>
                      <i v-else-if="row.itemType === 'product'" class="bi bi-box-seam vendor_create_bill-item-type-icon" aria-hidden="true"></i>
                      <i v-else-if="row.itemType === 'service'" class="bi bi-tools vendor_create_bill-item-type-icon" aria-hidden="true"></i>
                      <i v-else-if="row.itemType === 'Inventory'" class="bi bi-boxes vendor_create_bill-item-type-icon" aria-hidden="true"></i>
                      {{ row.itemName }}
                    </div>
                    <div class="vendor_create_bill-item-details">
                      <span v-if="row.itemType === 'consultant'">
                        Timesheet: {{ row.hours }}h × ${{ row.hourlyRate }} = ${{ row.amount.toFixed(2) }}
                      </span>
                      <span v-else-if="row.itemType === 'product' || row.itemType === 'service' || row.itemType === 'Inventory'">
                        {{ row.itemType }}: {{ row.quantity }} × ${{ row.unitPrice }} = ${{ row.amount.toFixed(2) }}
                      </span>
                      <span class="vendor_create_bill-source-info">({{ row.source }})</span>
                    </div>
                  </div>
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-auto-account-amount-${idx}`"
                    label="Amount (USD)"
                    v-model="row.amount"
                    type="number"
                    placeholder="0.00"
                    readonly
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--amount">{{ row.amount ? row.amount.toFixed(2) : '' }}</td>
                <td></td>
              </tr>
              
              <!-- Manual rows that users can add/edit -->
              <tr v-for="(row, idx) in accountRows" :key="'manual-account-' + idx">
                <td class="vendor_create_bill-table-cell--field">
                  <LabelDropdown
                    label="Account"
                    :select-id="`create-bill-manual-account-${idx}`"
                    v-model="row.accountId"
                    inline
                    wide
                    searchable
                    custom-class="vendor_create_bill-table-field"
                    menu-class="vendor-create-bill-dropdown-menu"
                    :options="accountDropdownOptions"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-account-desc-${idx}`"
                    label="Description"
                    v-model="row.description"
                    placeholder="Description"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-manual-account-amount-${idx}`"
                    label="Amount (USD)"
                    v-model="row.amount"
                    type="number"
                    placeholder="0.00"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--amount">{{ row.amount ? parseFloat(row.amount).toFixed(2) : '0.00' }}</td>
                <td class="vendor_create_bill-table-cell--actions">
                  <PrimaryButton label="Remove" icon="bi bi-trash" compact @click="removeAccountRow(idx)" />
                </td>
              </tr>
              <tr>
                <td colspan="5" class="vendor_create_bill-table-total-row">Total (USD): <span class="vendor_create_bill-line-total-value">$ {{ accountTotal.toFixed(2) }}</span></td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>
          <div class="vendor_create_bill-itemize-add-wrap">
            <PrimaryButton label="Add Account Row" icon="bi bi-plus-lg" compact @click="addAccountRow" />
          </div>
        </div>

        <!-- Itemize by Consultant Hours -->
        <div
          v-if="isConsultantIndustry"
          v-show="activeTab === 'consultant'"
          role="tabpanel"
          class="vendor_create_bill-itemize-section vendor_create_bill-tab-panel glass-tab-panel--nested"
          :class="{ 'vendor_create_bill-tab-panel--enter': tabPanelEnter === 'consultant' }"
          @animationend="onTabPanelAnimEnd($event, 'consultant')"
        >
          <div class="consultant-glass-table-section vendor_create_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
          <table class="vendor_create_bill-itemize-table consultant-glass-table">
            <thead>
              <tr>
                <th>Consultant</th>
                <th>Description</th>
                <th>Timesheet</th>
                <th>Hours</th>
                <th>Hourly Rate (USD)</th>
                <th>Total (USD)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(consultant, idx) in consultantHoursRows" :key="`${consultantHoursRowsKey}-${idx}`">
                <td class="vendor_create_bill-table-cell--field">
                  <LabelDropdown
                    label="Consultant"
                    :select-id="`create-bill-consultant-hours-${idx}`"
                    v-model="consultant.id"
                    inline
                    wide
                    searchable
                    custom-class="vendor_create_bill-table-field"
                    menu-class="vendor-create-bill-dropdown-menu"
                    :options="getConsultantHoursDropdownOptions()"
                    @change="(val) => handleConsultantHoursSelect(idx, val)"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-consultant-hours-desc-${idx}`"
                    label="Description"
                    v-model="consultant.description"
                    placeholder="Description"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field vendor_create_bill-table-cell--timesheet">
                  <template v-if="consultant.id">
                    <!-- Timesheet dropdown for consultants -->
                    <div class="timesheet-select-wrapper">
                      <LabelDropdown
                        label="Timesheet"
                        :select-id="`create-bill-consultant-timesheet-${idx}`"
                        v-model="consultant.selectedTimesheetId"
                        inline
                        wide
                        searchable
                        custom-class="vendor_create_bill-table-field"
                        menu-class="vendor-create-bill-dropdown-menu"
                        :options="getTimesheetDropdownOptions(idx)"
                        @change="(val) => handleConsultantHoursTimesheetSelect(idx, val)"
                      />
                    </div>
                    
                    <!-- Show timesheet info if selected and has attachments -->
                    <div v-if="(consultant.timesheetId || (consultant.timesheetData && consultant.timesheetData.id)) && consultant.timesheetData && consultant.timesheetData.attachments && consultant.timesheetData.attachments.length > 0" class="timesheet-info">
                      <div class="timesheet-attachments">
                        <span v-for="(attachment, attIdx) in consultant.timesheetData.attachments" :key="attIdx" class="attachment-badge" @click.stop="viewConsultantHoursAttachment(attachment, consultant.id)" style="cursor: pointer;" :title="'Click to view ' + attachment.fileName">
                          <i class="bi bi-paperclip"></i> {{ attachment.fileName }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <template v-else>-</template>
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-consultant-hours-hours-${idx}`"
                    label="Hours"
                    v-model="consultant.hours"
                    type="number"
                    placeholder="Hours"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-consultant-hours-rate-${idx}`"
                    label="Hourly Rate (USD)"
                    v-model="consultant.hourlyRate"
                    type="number"
                    placeholder="Rate"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--amount">{{ getConsultantHoursTotal(consultant) }}</td>
                <td class="vendor_create_bill-table-cell--actions">
                  <PrimaryButton label="Remove" icon="bi bi-trash" compact @click="removeConsultantHoursRow(idx)" />
                </td>
              </tr>
              <tr>
                <td colspan="7" class="vendor_create_bill-table-total-row">Total (USD): <span class="vendor_create_bill-line-total-value">$ {{ consultantHoursTotal.toFixed(2) }}</span></td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>
          <div class="vendor_create_bill-itemize-add-wrap">
            <PrimaryButton label="Add Consultant" icon="bi bi-person-plus" compact @click="addConsultantHoursRow" />
          </div>
        </div>

        <!-- Itemize by Product/Service -->
        <div
          v-show="activeTab === 'product'"
          role="tabpanel"
          class="vendor_create_bill-itemize-section vendor_create_bill-tab-panel glass-tab-panel--nested"
          :class="{ 'vendor_create_bill-tab-panel--enter': tabPanelEnter === 'product' }"
          @animationend="onTabPanelAnimEnd($event, 'product')"
        >
          <div class="vendor_create_bill-glass-hint">
            <i class="bi bi-info-circle" aria-hidden="true"></i>
            <div class="vendor-create-bill-item-filter-info">
              <small v-if="vendorId">Showing product, service and inventory items from the selected vendor only. Products and services go to expense accounts; inventory goes to its assets account and adds to on-hand stock.</small>
              <small v-else>Select a vendor to see their product, service and inventory items, or manually add items below.</small>
            </div>
          </div>

          <div class="consultant-glass-table-section vendor_create_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
          <table class="vendor_create_bill-itemize-table consultant-glass-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, idx) in productRows" :key="'product-' + idx">
                <!-- Main product/service row -->
                <tr>
                  <td class="vendor_create_bill-table-cell--field">
                    <LabelDropdown
                      label="Item"
                      :select-id="`create-bill-product-item-${idx}`"
                      v-model="row.selectedItem"
                      inline
                      wide
                      searchable
                      custom-class="vendor_create_bill-table-field"
                      menu-class="vendor-create-bill-dropdown-menu"
                      :options="getProductItemDropdownOptions(idx)"
                      @change="onProductServiceChange(row)"
                    />
                  </td>
                  <td class="vendor_create_bill-table-cell--field">
                    <LabelInputField
                      :input-id="`create-bill-product-desc-${idx}`"
                      label="Description"
                      v-model="row.description"
                      placeholder="Description"
                      flex
                      custom-class="vendor_create_bill-table-field"
                    />
                  </td>
                  <td><input type="number" v-model="row.quantity" class="vendor_create_bill-itemize-table-input" /></td>
                  <td><input type="number" v-model="row.unitPrice" class="vendor_create_bill-itemize-table-input" readonly /></td>
                  <td class="vendor_create_bill-table-cell--amount">{{ row.quantity && row.unitPrice ? (row.quantity * row.unitPrice).toFixed(2) : '' }}</td>
                  <td class="vendor_create_bill-table-cell--actions">
                    <PrimaryButton label="Remove" icon="bi bi-trash" compact @click="removeProductRow(idx)" />
                  </td>
                </tr>
                
              </template>
              
              <tr>
                <td colspan="6" class="vendor_create_bill-table-total-row">Total (USD): <span class="vendor_create_bill-line-total-value">$ {{ productServiceTotal.toFixed(2) }}</span></td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>
          <div v-if="showAddProductButton" class="vendor_create_bill-itemize-add-wrap">
            <PrimaryButton label="Add New Item" icon="bi bi-plus-lg" compact @click="addProductRow" />
          </div>
        </div>
      </div>

        <!-- Consultant timesheet records (auto-added from vendor) -->
        <div v-if="isConsultantIndustry && consultantTimesheets.length > 0" class="vendor_create_bill-consultant-section glass-tab-panel--nested">
        <div class="vendor_create_bill-consultant-header glass-tab-panel__title">
          <i class="bi bi-clock-history" aria-hidden="true"></i>
          Consultant Timesheet Records
        </div>
        <div class="vendor_create_bill-info-stack">
          <div class="vendor_create_bill-glass-hint vendor-create-bill-auto-info">
            <i class="bi bi-info-circle" aria-hidden="true"></i>
            <small>Available consultant timesheets are automatically added to the bill when selecting a vendor.</small>
          </div>
          <div
            v-if="consultantsLoading"
            class="vendor_create_bill-glass-status vendor_create_bill-glass-status--loading vendor-create-bill-consultant-loading"
            role="status"
            aria-live="polite"
          >
            <i class="bi bi-arrow-repeat vendor_create_bill-glass-status-spin" aria-hidden="true"></i>
            <span>Loading consultant timesheets…</span>
          </div>
          <div
            v-else-if="!consultantsLoading"
            class="vendor_create_bill-glass-status vendor_create_bill-glass-status--metrics vendor-create-bill-timesheet-count"
          >
            <i class="bi bi-bar-chart-line" aria-hidden="true"></i>
            <div class="vendor_create_bill-glass-status-body">
              <span class="vendor_create_bill-glass-metric">
                <span class="vendor_create_bill-glass-metric-label">Available timesheets</span>
                <span class="vendor_create_bill-glass-metric-value">{{ availableConsultantCount }}</span>
              </span>
              <span class="vendor_create_bill-glass-metric-divider" aria-hidden="true"></span>
              <span class="vendor_create_bill-glass-metric">
                <span class="vendor_create_bill-glass-metric-label">Added to bill</span>
                <span class="vendor_create_bill-glass-metric-value">{{ addedConsultantCount }}</span>
              </span>
              <span
                v-if="consultantTimesheets.length > 0 && availableConsultantCount === 0"
                class="vendor_create_bill-glass-status-note vendor-create-bill-consultant-success"
              >
                <i class="bi bi-check-circle" aria-hidden="true"></i>
                All timesheets auto-added
              </span>
              <span
                v-else-if="availableConsultantCount > 0"
                class="vendor_create_bill-glass-status-note vendor-create-bill-consultant-remaining"
              >
                {{ availableConsultantCount }} remaining to add
              </span>
            </div>
          </div>
        </div>
        
          <div class="consultant-glass-table-section vendor_create_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
          <table class="vendor_create_bill-itemize-table consultant-glass-table">
            <thead>
              <tr>
                <th>Consultant</th>
                <th>Description</th>
                <th>Hours</th>
                <th>Hourly Rate (USD)</th>
                <th>Timesheet</th>
                <th>Total (USD)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(consultant, idx) in consultantTimesheets" :key="idx" class="consultant-glass-table-row">
                <td class="vendor_create_bill-table-cell--field">
                  <LabelDropdown
                    v-if="consultantOptions.length > 0"
                    label="Consultant"
                    :select-id="`create-bill-timesheet-consultant-${idx}`"
                    v-model="consultant.name"
                    inline
                    wide
                    searchable
                    custom-class="vendor_create_bill-table-field"
                    menu-class="vendor-create-bill-dropdown-menu"
                    :options="getConsultantTimesheetDropdownOptions(idx)"
                    @change="onConsultantChange(idx)"
                  />
                  <input v-else v-model="consultant.name" type="text" placeholder="Consultant Name" class="vendor_create_bill-itemize-table-input" />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-timesheet-desc-${idx}`"
                    label="Description"
                    v-model="consultant.description"
                    placeholder="Description"
                    readonly
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-timesheet-hours-${idx}`"
                    label="Hours"
                    v-model="consultant.hours"
                    type="number"
                    placeholder="Hours"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td class="vendor_create_bill-table-cell--field">
                  <LabelInputField
                    :input-id="`create-bill-timesheet-rate-${idx}`"
                    label="Hourly Rate (USD)"
                    v-model="consultant.hourlyRate"
                    type="number"
                    placeholder="Rate"
                    flex
                    custom-class="vendor_create_bill-table-field"
                  />
                </td>
                <td>
                  <span v-if="consultant.attachment">
                    <a :href="getAttachmentUrl(consultant.attachment)" target="_blank" rel="noopener noreferrer" class="vendor_create_bill-timesheet-link">
                      <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
                      View Timesheet
                    </a>
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="vendor_create_bill-table-cell--amount">${{ getConsultantTotal(consultant) }}</td>
                <td class="vendor_create_bill-table-cell--actions">
                  <PrimaryButton label="Remove" icon="bi bi-trash" compact @click="removeConsultantTimesheet(idx)" />
                </td>
              </tr>
              <tr>
                <td colspan="7">
                  <div class="vendor_create_bill-total-wrapper">
                    Total (USD): <span class="vendor_create_bill-line-total-value">$ {{ consultantTimesheetTotal.toFixed(2) }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>
      </div>

      <div class="vendor_create_bill-comments-section">
        <div class="vendor_create_bill-form-group vendor_create_bill-form-group--label-description">
          <LabelDescriptionField
            input-id="create-bill-comments"
            label="Write comments for this Create Bill."
            v-model="comments"
            placeholder="Comments"
            flex
          />
        </div>
      </div>

      <!-- Attachments Section (reference: CustomerAddNewInvoice.vue) -->
      <div ref="billAttachmentsSection" class="vendor_create_bill-glass-panel vendor_create_bill-attachments-section glass-tab-panel--nested">
        <div class="vendor_create_bill-attachments-header">
          <i class="bi bi-paperclip" aria-hidden="true"></i>
          <span>Attachments</span>
        </div>
        <div v-if="attachments && attachments.length > 0" class="vendor_create_bill-attachments-list">
          <div
            v-for="(attachment, index) in attachments"
            :key="index"
            :class="['vendor_create_bill-attachment-item', { 'vendor_create_bill-attachment-item--compressing': compressingAttachmentIndex === index }]"
          >
            <span :class="['vendor_create_bill-attachment-icon', `vendor_create_bill-attachment-icon--${attachmentFileKind(attachment)}`]" aria-hidden="true">
              <i :class="attachmentFileIcon(attachment)"></i>
            </span>
            <div class="vendor_create_bill-attachment-info">
              <a href="#" @click.prevent="viewBillAttachment(attachment)" class="vendor_create_bill-attachment-link" :title="attachment.fileName">{{ attachment.fileName }}</a>
              <div class="vendor_create_bill-attachment-meta">
                <span class="vendor_create_bill-attachment-size">{{ formatFileSize(attachment.fileSize) }}</span>
                <span
                  v-if="compressingAttachmentIndex === index"
                  class="vendor_create_bill-compress-status vendor_create_bill-compress-status--compressing"
                >
                  <i class="bi bi-arrow-repeat vendor_create_bill-compress-spin" aria-hidden="true"></i>
                  Compressing…
                </span>
                <span v-else-if="attachmentCompressQueue.includes(attachment)" class="vendor_create_bill-compress-status">
                  Waiting…
                </span>
                <span
                  v-else-if="attachment.compressionResult"
                  :class="['vendor_create_bill-compress-status', `vendor_create_bill-compress-status--${attachment.compressionResult}`]"
                >
                  {{ compressResultLabel(attachment) }}
                </span>
              </div>
            </div>
            <div class="vendor_create_bill-attachment-buttons">
              <button
                v-if="canCompressAttachment(attachment) && compressingAttachmentIndex !== index && !attachmentCompressQueue.includes(attachment)"
                type="button"
                class="vendor_create_bill-compress-file-btn"
                title="Compress this file"
                :disabled="compressingAttachments"
                @click="compressBillAttachment(index)"
              >
                <i class="bi bi-file-earmark-zip" aria-hidden="true"></i>
                Compress
              </button>
              <button type="button" @click="removeBillAttachment(index)" class="vendor_create_bill-attachment-delete-btn" title="Remove attachment" aria-label="Remove attachment" :disabled="compressingAttachments">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="vendor_create_bill-attachment-actions">
          <label for="create-bill-attachment-input" class="vendor_create_bill-attach-file-link">
            <i class="bi bi-upload" aria-hidden="true"></i>
            Attach a file
            <input
              id="create-bill-attachment-input"
              type="file"
              ref="addBillFileInput"
              @change="handleBillFileUpload"
              multiple
            />
          </label>
          <span v-if="attachmentCompressNote" class="vendor_create_bill-compress-note">{{ attachmentCompressNote }}</span>
        </div>
      </div>

      <div class="vendor_create_bill-total-section glass-tab-panel--nested">
        <span>Total (USD)</span>
        <span class="vendor_create_bill-total-value">$ {{ finalTotal.toFixed(2) }}</span>
      </div>
      <div class="vendor_create_bill-form-actions vendor_create_bill-action-buttons">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" :disabled="createBillSubmitDisabled" @click="handleCancel" />
        <PrimaryButton label="Save and New" icon="bi bi-plus-lg" :disabled="createBillSubmitDisabled" @click="handleSaveAndNew" />
        <PrimaryButton label="Save and Close" icon="bi bi-check-lg" :disabled="createBillSubmitDisabled" @click="handleSaveAndClose" />
      </div>
    </form>
    <AddNewPaymentTerm
      :visible="showAddPaymentTermModal"
      :companyId="$route.params.id"
      @ok="onAddPaymentTermOk"
      @cancel="showAddPaymentTermModal = false"
    />
    <SuccessModal
      :show="successModalVisible"
      :title="successModalTitle"
      :message="successModalMessage"
      :buttonText="successModalButtonText"
      @close="successModalVisible = false"
    />
    
    <!-- Error Modal for validation -->
    <ErrorModal
      :show="showErrorModal"
      :title="errorModalTitle"
      :message="errorModalMessage"
      :buttonText="errorModalButtonText"
      :action-button-text="errorModalActionButtonText"
      @close="onErrorModalClose"
      @action="onErrorModalAction"
    />
    
    <!-- AddTimeSheet Modal -->
    <div v-if="showTimesheetModal" class="vendor-create-bill-modal-overlay">
      <div class="vendor-create-bill-modal" @click.stop>
        <AddTimeSheet 
          :prefilled-consultant="prefilledConsultantData"
          @close="closeTimesheetModal"
          @success="handleTimesheetSuccess"
        />
      </div>
    </div>
    
    <!-- Vendor Creation Dialog Modal -->
    <VendorCreationDialog
      :visible="showVendorCreationDialog"
      :companyId="$route.params.id"
      @ok="onVendorCreated"
      @cancel="showVendorCreationDialog = false"
      @close="showVendorCreationDialog = false"
    />

    <!-- Consultant Creation Dialog Modal -->
    <ConsultantCreationDialog
      :visible="showConsultantCreationDialog"
      :companyId="$route.params.id"
      :forcedConsultantType="'C2C'"
      :lockConsultantType="true"
      :prefilledVendorId="vendorId || null"
      :lockVendor="!!vendorId"
      :close-on-overlay-click="false"
      @ok="onConsultantCreated"
      @cancel="showConsultantCreationDialog = false"
      @close="showConsultantCreationDialog = false"
    />
  </div>

  <Teleport to="body">
    <Transition name="vendor-create-bill-info-popover">
      <div
        v-if="accountInfoOpen"
        ref="accountInfoPopover"
        class="vendor_create_bill-account-info-popover"
        :style="accountInfoPopoverStyle"
        role="tooltip"
        @click.stop
      >
        <p class="vendor_create_bill-account-info-popover-text">
          Each product/service item<span v-if="isConsultantIndustry"> and consultant timesheet</span> appears as an individual account entry with its own amount. No grouping or summing.
        </p>
        <p class="vendor_create_bill-account-info-popover-text">
          <strong>Available Account Types:</strong> Expense, Other Expense, Cost of Goods Sold, Current Liability, Payroll Liability
        </p>
        <p class="vendor_create_bill-account-info-popover-text">
          <strong>Purpose:</strong> Categorize vendor bills into specific accounting accounts for proper financial reporting and tracking.
        </p>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import '../styles/CreateBill.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import { extractSummaryTotal, extractCapturedBillFields } from '@/modules/vendor/utils/formatBillAttachmentSummary';
import { notifyCompanyBillSaved } from '@/services/billCompanyNotification.service.js';
import vendorService from '@/services/vendor.service.js';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import BillJournalEntry from '@/modules/company/views/BillJournalEntry.vue';
import CreateBillProgressor from '@/components/CreateBillProgressor.vue';
import AddTimeSheet from '@/modules/consultant/views/AddTimeSheet.vue';
import VendorCreationDialog from '@/modules/vendor/views/VendorCreationDialog.vue';
import ConsultantCreationDialog from '@/modules/customer/views/ConsultantCreationDialog.vue';
import {
  attachmentsExceedBillLimit,
  buildBillAttachmentLimitMessage,
  describeAttachmentCompression,
  getAttachmentsTotalBytes,
  resolveBillSaveAlert,
} from '@/modules/vendor/utils/billSaveAlert.js';
import { compressSingleAttachment, isCompressibleAttachment } from '@/modules/vendor/utils/attachmentCompression.js';
import { isConsultantIndustry as isConsultantIndustryId } from '@/constants/companyIndustryTypes';

export default {
  name: 'CreateBill',
  components: {
    AddNewPaymentTerm,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    BillJournalEntry,
    CreateBillProgressor,
    AddTimeSheet,
    VendorCreationDialog,
    ConsultantCreationDialog,
    LabelInputField,
    LabelDropdown,
    LabelDescriptionField,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      activeTab: 'product', // Default; consultant industries switch in applyDefaultItemizeTab
      tabPanelEnter: '',
      accountInfoOpen: false,
      accountInfoPopoverStyle: null,
      vendorId: '',
      contactName: '',
      phone: '',
      paymentTerm: '',
      paymentTermsList: [],
      previousPaymentTerm: '',
      showAddPaymentTermModal: false,
      billDate: '',
      dueDate: '',
      deliveryDate: '',
      invNo: '',
      serialNo: 1,
      vendorList: [],
      contactList: [],
      accountList: [],
      allCompanyAccounts: [],
      productServiceList: [],
      vendorBills: [],
      showAccountItems: true,
      showProductItems: true,
      accountRows: [ { accountId: '', description: '', amount: 0 } ],
      productRows: [ { itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 } ],
      comments: '',
      payFrom: '',
      payFromList: [],
      payFromBalanceError: '',
      // Consultant timesheet properties
      showConsultantTimesheets: true,
      consultantsLoading: false,
      consultantsError: '',
      consultantOptions: [],
      consultantTimesheets: [],
      allConsultantOptions: [], // Store all options for "Select All" functionality
      expenseAccountMappings: [], // Store account mappings with type information for amount calculations
      // Consultant hours properties (for Itemize by Consultant Hours section)
      consultantHoursRows: [{ id: '', description: '', hours: 0, hourlyRate: 0, timesheetId: null, timesheetData: null, availableTimesheets: [], selectedTimesheetId: '' }],
      consultantHoursRowsKey: 0,
      allCompanyConsultants: [], // All consultants for the company
      // Success modal properties
      successModalVisible: false,
      successModalTitle: 'Success',
      successModalMessage: '',
      successModalButtonText: 'OK',
      // Error modal properties
      showErrorModal: false,
      errorModalTitle: 'Validation Error',
      errorModalMessage: '',
      errorModalButtonText: 'OK',
      errorModalActionKey: null,
      errorModalActionButtonText: '',
      journalAutoCreateActive: false,
      createBillPipeline: null,
      createBillProgressorShow: false,
      createBillProgressorSteps: [],
      createBillProgressorOkEnabled: false,
      createBillProgressorFootMessage: '',
      createBillProgressorFootTone: 'neutral',
      savedBillId: null, // Store the saved bill ID for journal entry reference
      previousVendorId: null, // Track previous vendor to detect vendor changes
      routeConsultantPrefillApplied: false,
      editingBillId: null,
      loadingExistingBill: false,
      /** Timesheet IDs already on Draft bills for the selected vendor (invoice/portal drafts). */
      draftLinkedTimesheetIds: [],
      draftLinkedTimesheetIdsVendorId: null,
      draftLinkedTimesheetIdsExcludeBillId: null,
      /** When true, auto-draft bill nos (DRAFT-…) are replaced with vendor+date bill nos while placing. */
      replaceDraftBillNo: false,
      capturedReview: null,
      capturedInvoiceNo: '',
      originalDraftSnapshot: null,
      // Timesheet modal properties
      showTimesheetModal: false,
      prefilledConsultantData: null,
      currentTimesheetIndex: null,
      // Vendor creation dialog
      showVendorCreationDialog: false,
      // Consultant creation dialog
      showConsultantCreationDialog: false,
      currentConsultantHoursIndex: null,
      // Bill-level attachments (reference: CustomerAddNewInvoice.vue)
      attachments: [],
      attachmentPreviewUrls: {},
      compressingAttachmentIndex: null,
      attachmentCompressQueue: [],
      attachmentCompressNote: ''
    };
  },
  computed: {
    compressingAttachments() {
      return this.compressingAttachmentIndex !== null || this.attachmentCompressQueue.length > 0;
    },
    billNoFieldStyle() {
      const text = String(this.invNo || '').trim() || 'Bill No';
      const chars = Math.max(8, text.length + 1);
      return { '--create-bill-no-ch': String(chars) };
    },
    capturedReviewHint() {
      if (!this.$route.query.fromCaptured && !this.capturedReview) return '';
      const total = this.capturedReviewTotalLabel;
      const relatedCount = String(this.$route.query.relatedBillIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean).length;
      if (total && relatedCount) {
        return `This draft combines ${relatedCount + 1} selected bills for captured mail (${total}). Review the lines, then save.`;
      }
      if (total) {
        return `This draft is matched to captured mail (${total}). Adjust hours, dates, or amount to match the invoice, then save.`;
      }
      return 'This draft is matched to captured mail. Adjust the form to match the invoice, then save.';
    },
    capturedReviewTotalLabel() {
      const raw = this.capturedReview?.summary || '';
      const amount = extractSummaryTotal(raw);
      if (amount == null) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    },
    effectiveIndustryId() {
      const company = this.$store?.state?.currentCompany;
      return company?.industryId ?? company?.cp_industry_type;
    },
    isConsultantIndustry() {
      return isConsultantIndustryId(this.effectiveIndustryId);
    },
    vendorDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.activeVendors || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.vendorName,
        })),
        { value: '__create_new__', label: 'Create new Vendor' },
      ];
    },
    contactDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.contactList || []).map((contact) => ({
          value: contact.name,
          label: `${contact.name}${contact.email ? ` (${contact.email})` : ''}${contact.primary ? ' [Primary]' : ''}`,
        })),
      ];
    },
    paymentTermDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.paymentTermsList || []).map((term) => ({
          value: String(term.id),
          label: `${term.name} (${term.description})`,
        })),
        { value: '__add_new__', label: 'Add New Payment Term' },
      ];
    },
    accountDropdownOptions() {
      return [
        { value: '', label: 'Select Account' },
        ...(this.accountList || []).map((account) => ({
          value: String(account.id),
          label: `${account.accountNumber ? `${account.accountNumber} - ` : ''}${account.accountName}${account.accountType ? ` - ${account.accountType}` : ''}`,
        })),
      ];
    },
    // Read-only auto rows can point at asset accounts (inventory purchases), which accountList excludes.
    autoAccountDropdownOptions() {
      const accounts = this.allCompanyAccounts && this.allCompanyAccounts.length
        ? this.allCompanyAccounts
        : (this.accountList || []);
      return [
        { value: '', label: 'Select Account' },
        ...accounts.map((account) => ({
          value: String(account.id),
          label: `${account.accountNumber ? `${account.accountNumber} - ` : ''}${account.accountName}${account.accountType ? ` - ${account.accountType}` : ''}`,
        })),
      ];
    },
    // Check if there are any source records (consultant timesheets or product/service items)
    hasSourceRecords() {
      const hasConsultants =
        this.isConsultantIndustry &&
        this.consultantTimesheets &&
        this.consultantTimesheets.length > 0;
      const hasProducts = this.productServiceList && this.productServiceList.length > 0;
      return hasConsultants || hasProducts;
    },
    // Create individual account rows for each Product/Service item and Consultant timesheet
    visibleAccountRows() {
      const individualRows = [];
      
      // Add individual rows for each Product/Service item
      this.productRows.forEach((row, rowIndex) => {
        // Show account row if item is selected (itemId and itemType exist) and has unitPrice
        // Even if quantity is 0, show the account row so user can see which account will be used
        if (row.itemId && row.itemType && row.unitPrice) {
          const quantity = Number(row.quantity) || 0;
          const unitPrice = Number(row.unitPrice) || 0;
          const amount = quantity * unitPrice;

          if (row.itemType === 'Inventory') {
            const inventoryItem = this.productServiceList.find(
              i => i.type === 'Inventory' && String(i.id) === String(row.itemId)
            );
            if (inventoryItem && inventoryItem.assetsAccountId) {
              individualRows.push({
                accountId: String(inventoryItem.assetsAccountId),
                description: `Inventory Purchase - ${row.description || inventoryItem.name || 'Unnamed Item'}`,
                amount: amount,
                itemType: row.itemType,
                itemName: row.description || inventoryItem.name || 'Unnamed Item',
                quantity: quantity,
                unitPrice: unitPrice,
                rowIndex: rowIndex,
                source: `Product/Service Row ${rowIndex + 1}`
              });
            }
            return;
          }
          
          // Find the expense account for this item
          // Use String comparison to handle type mismatches (number vs string)
          const mapping = this.expenseAccountMappings.find(m => {
            if (m.type !== 'product' && m.type !== 'service') return false;
            
            if (m.type === 'product') {
              return String(m.productItemId) === String(row.itemId);
            } else if (m.type === 'service') {
              return String(m.serviceItemId) === String(row.itemId);
            }
            return false;
          });
          
          if (mapping) {
            individualRows.push({
              accountId: mapping.accountId,
              description: `${row.itemType} Expense - ${row.description || 'Unnamed Item'}`,
              amount: amount,
              itemType: row.itemType,
              itemName: row.description || 'Unnamed Item',
              quantity: quantity,
              unitPrice: unitPrice,
              rowIndex: rowIndex,
              source: `Product/Service Row ${rowIndex + 1}`
            });
          } else {
            // Log when mapping is not found for debugging
            logger.warn(`No expense account mapping found for item:`, {
              itemId: row.itemId,
              itemType: row.itemType,
              availableMappings: this.expenseAccountMappings.filter(m => m.type === row.itemType)
            });
          }
        }
      });
      
      // Add individual rows for each Consultant timesheet (consultant industries only)
      if (this.isConsultantIndustry) {
        this.consultantTimesheets.forEach((consultant, consultantIndex) => {
          if (consultant.timesheetId) {
            const hours = Number(consultant.hours) || 0;
            const hourlyRate = Number(consultant.hourlyRate) || 0;
            const amount = hours * hourlyRate;
            
            if (amount > 0) {
              const mapping = this.resolveConsultantExpenseMapping(consultant.id, {
                consultantCustomerServiceId: consultant.consultantCustomerServiceId,
                consultantCustomerService: consultant.consultantCustomerService
              });

              if (mapping) {
                individualRows.push({
                  accountId: mapping.accountId,
                  description: `Consultant Expense - ${consultant.name}`,
                  amount: amount,
                  itemType: 'consultant',
                  itemName: consultant.name,
                  hours: hours,
                  hourlyRate: hourlyRate,
                  consultantIndex: consultantIndex,
                  source: `Consultant Timesheet ${consultantIndex + 1}`
                });
              }
            }
          }
        });
        
        // Add individual rows for each Consultant Hours entry (from Itemize by Consultant Hours section)
        this.consultantHoursRows.forEach((consultant, consultantIndex) => {
          if (consultant.id && consultant.timesheetId) {
            const hours = Number(consultant.hours) || 0;
            const hourlyRate = Number(consultant.hourlyRate) || 0;
            const amount = hours * hourlyRate;
            
            if (amount > 0) {
              const mapping = this.resolveConsultantExpenseMapping(consultant.id, {
                consultantCustomerServiceId:
                  consultant.timesheetData?.consultantCustomerServiceId ||
                  consultant.timesheetData?.consultant_customer_service_id,
                consultantCustomerService: consultant.timesheetData?.consultantCustomerService
              });

              if (mapping) {
                individualRows.push({
                  accountId: mapping.accountId,
                  description: `Consultant Expense - ${consultant.description || 'Consultant Hours'}`,
                  amount: amount,
                  itemType: 'consultant',
                  itemName: consultant.description || 'Consultant Hours',
                  hours: hours,
                  hourlyRate: hourlyRate,
                  consultantIndex: consultantIndex,
                  source: `Consultant Hours Row ${consultantIndex + 1}`
                });
              }
            }
          }
        });
      }
      
      return individualRows;
    },
    // Active vendors only (excludes inactive)
    activeVendors() {
      return (this.vendorList || []).filter(v => v.active !== false);
    },
    accountTotal() {
      // Sum both auto-populated rows and manual rows
      const autoTotal = this.visibleAccountRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
      const manualTotal = this.accountRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
      return autoTotal + manualTotal;
    },
    productServiceTotal() {
      if (!this.productRows || !Array.isArray(this.productRows)) {
        return 0;
      }
      return this.productRows.reduce((sum, row) => {
        if (row && row.quantity != null && row.unitPrice != null) {
          return sum + ((Number(row.quantity) || 0) * (Number(row.unitPrice) || 0));
        }
        return sum;
      }, 0);
    },
    consultantTimesheetTotal() {
      if (!this.isConsultantIndustry) return 0;
      return this.consultantTimesheets.reduce((sum, consultant) => {
        const hours = Number(consultant.hours) || 0;
        const hourlyRate = Number(consultant.hourlyRate) || 0;
        return sum + (hours * hourlyRate);
      }, 0);
    },
    consultantHoursTotal() {
      if (!this.isConsultantIndustry) return 0;
      return this.consultantHoursRows.reduce((sum, consultant) => {
        const hours = Number(consultant.hours) || 0;
        const hourlyRate = Number(consultant.hourlyRate) || 0;
        return sum + (hours * hourlyRate);
      }, 0);
    },
    finalTotal() {
      // Sum all sections: Consultant Hours (consultant industries), Product/Service, and manual Account rows
      const manualAccountTotal = Array.isArray(this.accountRows)
        ? this.accountRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
        : 0;
      return this.consultantHoursTotal + this.productServiceTotal + manualAccountTotal;
    },
    // Count of available consultant timesheets (not yet added to bill)
    availableConsultantCount() {
      return this.getAvailableConsultantOptions().length;
    },
    // Count of consultant timesheets added to bill
    addedConsultantCount() {
      return this.consultantTimesheets.filter(c => c.timesheetId).length;
    },
    // Check if bill has minimum data for journal entry
    hasMinimumDataForJournal() {
      // For journal entry, we need at least vendor and date
      return this.vendorId && this.billDate;
    },
    // Prepare bill data for journal entry modal
    billDataForJournal() {
      // Combine all valid account records (both auto-populated and manual)
      const allAccountRecords = [
        // Auto-populated account records from consultants/product-service
        ...this.visibleAccountRows
          .filter(row => row.accountId && row.amount && row.amount > 0)
          .map(row => ({
            accountId: row.accountId,
            description: row.description,
            amount: Number(row.amount) || 0,
            itemType: row.itemType || null
          })),
        // Manual account records
        ...this.accountRows
          .filter(row => row.accountId && row.amount && row.amount > 0)
          .map(row => ({
            accountId: row.accountId,
            description: row.description,
            amount: Number(row.amount) || 0,
            itemType: row.itemType || null
          }))
      ];
      
      // Service Date automatically uses Bill Date
      const deliveryDateToUse = this.deliveryDate || this.billDate;
      return {
        billId: this.savedBillId, // Include saved bill ID for journal entry reference
        billNo: this.invNo,
        billDate: this.billDate,
        date: this.billDate,
        dueDate: this.dueDate,
        deliveryDate: deliveryDateToUse,
        vendorName: this.selectedVendor?.vendorName || 'Vendor',
        vendor: this.selectedVendor,
        totalAmount: this.finalTotal > 0 ? this.finalTotal : this.accountTotal,
        subtotal: this.finalTotal > 0 ? this.finalTotal : this.accountTotal,
        comments: this.comments,
        accountRecords: allAccountRecords // All valid account records for debit entries
      };
    },
    selectedVendor() {
      return this.vendorList.find(v => String(v.id) === String(this.vendorId));
    },
    selectedPaymentTermDays() {
      const selectedTerm = this.paymentTermsList.find(t => String(t.id) === String(this.paymentTerm));
      if (selectedTerm && selectedTerm.dueDays != null && !isNaN(Number(selectedTerm.dueDays))) {
        return Number(selectedTerm.dueDays);
      }
      return null;
    },
    showAddProductButton() {
      // Always show add button to allow manual item addition
      return true;
    },
    createBillSubmitDisabled() {
      return (
        (this.createBillProgressorShow && !this.createBillProgressorOkEnabled) ||
        this.journalAutoCreateActive ||
        this.compressingAttachments
      );
    },
  },
  async created() {
    const draftBillId = this.$route.query.billId;
    this.loadingExistingBill = !!draftBillId;
    if (draftBillId) this.editingBillId = draftBillId;

    // Initialize billDate with current date
    this.billDate = this.formatDateForDatePicker(new Date());
    // Service Date automatically uses Bill Date
    this.deliveryDate = this.billDate;
    
    // Initialize with default rows since tables are always visible
    this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
    this.accountRows = [{ accountId: '', description: '', amount: 0 }];
    this.consultantHoursRows = [{ id: '', description: '', hours: 0, hourlyRate: 0, timesheetId: null, timesheetData: null, availableTimesheets: [], selectedTimesheetId: '' }];
    
    try {
      await this.fetchVendorList();

      const prefillVendorId = this.$route.query.prefillVendor
        ? String(this.$route.query.prefillVendor)
        : '';
      if (prefillVendorId) {
        const exists = this.vendorList.some((v) => String(v.id) === prefillVendorId);
        if (exists) {
          this.vendorId = prefillVendorId;
        } else {
          logger.warn(`Create Bill: prefill vendor ${prefillVendorId} not found in vendor list`);
        }
      }

      await this.fetchPaymentTerms();
      await this.fetchAccounts();
      await this.fetchProductServiceItems(prefillVendorId || null);
      await this.fetchAllConsultantsForCompany(prefillVendorId || null);

      this.applyDefaultItemizeTab();

      this.$nextTick(() => {
        this.tabPanelEnter = this.activeTab;
      });

      const companyId = this.$route.params.id;
      const res = await ApiService.getCompanyAccounts(companyId);
      this.payFromList = (res.data.accounts || res.data || []).filter(a => [
        'Cash', 'Bank', 'Paypal', 'Credit Card', 'Other current Asset'
      ].includes(a.accountType));

      if (draftBillId) {
        await this.loadDraftBillForAdjust(draftBillId);
      }
    } catch (err) {
      logger.error('Create Bill failed to load:', err);
    } finally {
      this.loadingExistingBill = false;
    }
  },
  methods: {
    setActiveTab(tab) {
      if (tab === 'consultant' && !this.isConsultantIndustry) {
        tab = 'product';
      }
      if (this.activeTab === tab) return;
      this.closeAccountInfo();
      this.activeTab = tab;
      this.tabPanelEnter = tab;
    },
    /** Consultant Hours is the default for consultant industries; otherwise Product/Service. */
    applyDefaultItemizeTab() {
      const qTab = String(this.$route?.query?.activeTab || '').trim().toLowerCase();
      if (qTab === 'account' || qTab === 'product') {
        this.activeTab = qTab;
        return;
      }
      if (qTab === 'consultant' && this.isConsultantIndustry) {
        this.activeTab = 'consultant';
        return;
      }
      this.activeTab = this.isConsultantIndustry ? 'consultant' : 'product';
    },
    onTabPanelAnimEnd(event, tab) {
      if (event?.animationName !== 'create-bill-glass-tab-panel-in') return;
      if (this.tabPanelEnter === tab) {
        this.tabPanelEnter = '';
      }
    },
    toggleAccountInfo(event) {
      if (this.accountInfoOpen) {
        this.closeAccountInfo();
        return;
      }
      this.updateAccountInfoPopoverPosition(event);
      this.accountInfoOpen = true;
      this.$nextTick(() => {
        document.addEventListener('click', this.closeAccountInfoOnOutside, true);
        window.addEventListener('scroll', this.updateAccountInfoPopoverOnViewportChange, true);
        window.addEventListener('resize', this.updateAccountInfoPopoverOnViewportChange);
      });
    },
    updateAccountInfoPopoverPosition(event) {
      const btn = event?.currentTarget || this.$refs.accountInfoTrigger?.querySelector('button');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 8;
      const maxWidth = 320;
      const left = Math.min(rect.left, window.innerWidth - maxWidth - 12);
      this.accountInfoPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${Math.max(12, left)}px`,
        zIndex: 10050,
      };
    },
    updateAccountInfoPopoverOnViewportChange() {
      if (!this.accountInfoOpen) return;
      const btn = this.$refs.accountInfoTrigger?.querySelector('button');
      if (!btn) return;
      this.updateAccountInfoPopoverPosition({ currentTarget: btn });
    },
    closeAccountInfoOnOutside(event) {
      const trigger = this.$refs.accountInfoTrigger;
      const popover = this.$refs.accountInfoPopover;
      if (trigger?.contains(event.target) || popover?.contains(event.target)) return;
      this.closeAccountInfo();
    },
    closeAccountInfo() {
      if (!this.accountInfoOpen) return;
      this.accountInfoOpen = false;
      document.removeEventListener('click', this.closeAccountInfoOnOutside, true);
      window.removeEventListener('scroll', this.updateAccountInfoPopoverOnViewportChange, true);
      window.removeEventListener('resize', this.updateAccountInfoPopoverOnViewportChange);
    },
    getConsultantHoursDropdownOptions() {
      return [
        { value: '', label: 'Select Consultant' },
        ...this.getAvailableConsultantsForHours().map((consultantOption) => ({
          value: String(consultantOption.id),
          label: consultantOption.consultantName || consultantOption.name,
        })),
        { value: '__create_consultant__', label: '+ Add New Consultant' },
      ];
    },
    getTimesheetDropdownOptions(idx) {
      const consultant = this.consultantHoursRows[idx];
      const timesheets = consultant?.availableTimesheets || [];
      return [
        { value: '', label: 'Select Timesheet' },
        ...timesheets.map((ts) => ({
          value: String(ts.id),
          label: this.formatTimesheetOption(ts),
        })),
        { value: '__create_timesheet__', label: '+ Create New Timesheet' },
      ];
    },
    getProductItemDropdownOptions(idx) {
      return [
        { value: '', label: 'Select' },
        ...this.getAvailableItemsForRow(idx).map((item) => ({
          value: JSON.stringify({ id: item.id, type: item.type }),
          label: `${item.name} - ${item.type}`,
        })),
      ];
    },
    getConsultantTimesheetDropdownOptions(idx) {
      const filtered = this.consultantOptions.filter(
        (opt) => !this.consultantTimesheets.some(
          (c, i) => c.name === opt.name && c.timesheetId === opt.timesheetId && i !== idx
        )
      );
      return [
        { value: '', label: 'Select Consultant' },
        ...filtered.map((opt) => ({
          value: opt.name,
          label: `${opt.name} (${opt.description})`,
        })),
      ];
    },
    showSuccessModal(title, message, buttonText = 'OK') {
      this.successModalTitle = title;
      this.successModalMessage = message;
      this.successModalButtonText = buttonText;
      this.successModalVisible = true;
    },
    showAlertModal(title, message, buttonText = 'OK', actionKey = null) {
      this.errorModalTitle = title || 'Alert';
      this.errorModalMessage = message || '';
      this.errorModalButtonText = buttonText;
      this.errorModalActionKey = actionKey || null;
      this.errorModalActionButtonText = '';
      this.showErrorModal = true;
    },
    showBillSaveAlert(err) {
      const alert = resolveBillSaveAlert(err, { attachments: this.attachments });
      this.showAlertModal(alert.title, alert.message, 'OK', alert.actionKey);
      this.errorModalActionButtonText = alert.actionButtonText || '';
    },
    onErrorModalClose() {
      this.showErrorModal = false;
      this.errorModalActionKey = null;
      this.errorModalActionButtonText = '';
    },
    onErrorModalAction() {
      const actionKey = this.errorModalActionKey;
      this.onErrorModalClose();
      if (actionKey === 'compress-files') {
        this.compressBillAttachmentsForReview();
        return;
      }
      const companyId = this.$route.params.id;
      if (!companyId || !this.$router) return;
      if (actionKey === 'drafts-captured') {
        this.$router.push({
          name: 'DraftBills',
          params: { id: String(companyId) },
          query: { tab: 'drafts' },
        }).catch(() => {});
        return;
      }
      if (actionKey === 'invites-submitted') {
        this.$router.push({
          name: 'VendorPortalHub',
          params: { id: String(companyId) },
        }).catch(() => {});
      }
    },
    async fetchVendorList() {
      try {
        const companyId = this.$route.params.id;
        const data = await ApiService.getAllVendors(companyId);
        this.vendorList = Array.isArray(data) ? data : [];
        return this.vendorList;
      } catch (err) {
        this.vendorList = [];
        return [];
      }
    },
    async fetchPaymentTerms() {
      try {
        const companyId = this.$route.params.id;
        const termsRes = await ApiService.get(`/company/payment-terms?companyId=${companyId}`);
        this.paymentTermsList = Array.isArray(termsRes.data?.paymentTerms) ? termsRes.data.paymentTerms : [];
        return this.paymentTermsList;
      } catch (err) {
        logger.error('Failed to load payment terms:', err);
        this.paymentTermsList = Array.isArray(this.paymentTermsList) ? this.paymentTermsList : [];
        return this.paymentTermsList;
      }
    },
    async populateVendorDefaults(vendorId, { overwriteHeader = true } = {}) {
      if (!vendorId) return;

      try {
        const response = await vendorService.getVendorById(vendorId);
        const vendorData = response.data?.data || response.data;

        if (!vendorData) {
          return;
        }

        if (Array.isArray(vendorData.contacts) && vendorData.contacts.length > 0) {
          this.contactList = vendorData.contacts;
        }
        this.applyDefaultVendorContact();

        const vendorPaymentTermId =
          vendorData.paymentTermId ||
          vendorData.paymentTerm?.id ||
          vendorData.paymentTerm ||
          vendorData.defaultPaymentTermId;

        // Always fill payment term when missing (e.g. auto drafts created without one).
        // Only overwrite an existing value when overwriteHeader is true.
        if (vendorPaymentTermId && (overwriteHeader || !this.paymentTerm)) {
          this.applyPaymentTermId(vendorPaymentTermId);
        }
      } catch (error) {
        logger.error('Failed to populate vendor defaults:', error);
      }
    },
    applyPaymentTermId(paymentTermId) {
      if (paymentTermId == null || paymentTermId === '') return false;
      const id = String(paymentTermId);
      const matching = (this.paymentTermsList || []).find((term) => String(term.id) === id);
      if (!matching && (this.paymentTermsList || []).length) {
        logger.warn(`Payment term ID ${id} not found in payment terms list`);
        return false;
      }
      this.paymentTerm = id;
      this.previousPaymentTerm = id;
      return true;
    },
    async applyVendorPreferredPaymentTerm(vendorId = this.vendorId) {
      if (!vendorId || this.paymentTerm) return false;

      const fromList = this.vendorList.find((v) => String(v.id) === String(vendorId));
      const listTermId =
        fromList?.paymentTermId ||
        fromList?.paymentTerm?.id ||
        fromList?.paymentTerm ||
        null;
      if (listTermId && this.applyPaymentTermId(listTermId)) return true;

      try {
        const response = await vendorService.getVendorById(vendorId);
        const vendorData = response.data?.data || response.data;
        const vendorPaymentTermId =
          vendorData?.paymentTermId ||
          vendorData?.paymentTerm?.id ||
          vendorData?.paymentTerm ||
          vendorData?.defaultPaymentTermId;
        return this.applyPaymentTermId(vendorPaymentTermId);
      } catch (error) {
        logger.warn('Could not apply vendor preferred payment term:', error);
        return false;
      }
    },
    applyDefaultVendorContact({ force = false } = {}) {
      if (!Array.isArray(this.contactList) || this.contactList.length === 0) {
        const vendor = this.vendorList.find((v) => String(v.id) === String(this.vendorId));
        if (Array.isArray(vendor?.contacts) && vendor.contacts.length) {
          this.contactList = vendor.contacts;
        }
      }
      if (!Array.isArray(this.contactList) || this.contactList.length === 0) return;

      const listed = this.contactName
        ? this.contactList.find((c) => String(c.name) === String(this.contactName))
        : null;
      if (!force && listed) {
        if (!this.phone) this.phone = listed.phone || '';
        return;
      }

      const primary = this.contactList.find((c) => c.primary) || this.contactList[0];
      if (!primary) return;
      this.contactName = primary.name || '';
      this.phone = primary.phone || '';
    },
    handlePaymentTermChange(valueOrEvent) {
      const selected =
        valueOrEvent && typeof valueOrEvent === 'object' && valueOrEvent.target
          ? valueOrEvent.target.value
          : valueOrEvent;
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        this.$nextTick(() => {
          this.paymentTerm = this.previousPaymentTerm;
        });
      } else {
        this.paymentTerm = selected;
        this.previousPaymentTerm = selected;
      }
    },
    async onAddPaymentTermOk(newTerm) {
      this.showAddPaymentTermModal = false;
      await this.fetchPaymentTerms();
      if (newTerm && newTerm.id) {
        this.paymentTerm = String(newTerm.id);
        this.previousPaymentTerm = String(newTerm.id);
        this.showSuccessModal('Success', 'Payment term created!');
      }
    },
    async fetchAccounts() {
      const companyId = this.$route.params.id;
      const res = await ApiService.getCompanyAccounts(companyId);
      const allAccounts = res.data.accounts || res.data || [];
      const allowedTypes = [
        'Expense',
        'Other Expense',
        'Cost of Goods Sold',
        'Fixed Asset',
        'Current Liability',
        'Payroll Liability'
      ];
      this.allCompanyAccounts = Array.isArray(allAccounts) ? allAccounts : [];
      this.accountList = allAccounts.filter(a => allowedTypes.includes(a.accountType));
    },
    async fetchProductServiceItems(vendorId = null) {
      const companyId = this.$route.params.id;
      
      let vendorProductList = [];
      let vendorServiceList = [];
      
      // Always fetch ALL product and service items - using same API endpoints as VendorItemsList.vue
      try {
        const productRes = await ApiService.get(`/vendor/product-items/company/${companyId}`);
        logger.log('Product API response:', productRes);
        logger.log('Product data:', productRes.data);
        let filteredProducts = (productRes.data || [])
          .filter(item => item.billStatus === 'Available'); // Only show items with billStatus = 'Available'
        
        // Filter by vendor if vendorId is provided
        if (vendorId) {
          filteredProducts = filteredProducts.filter(item => 
            item.preferredVendor && String(item.preferredVendor) === String(vendorId)
          );
          logger.log(`Filtered products for vendor ${vendorId}: ${filteredProducts.length} items`);
        }
        
        vendorProductList = filteredProducts.map(item => ({
            id: item.id,
            name: item.productName,
            type: item.type || 'Product',
            salesPrice: item.salesPrice,
            salesDescription: item.salesDescription,
            purchasePrice: item.purchasePrice,
            purchaseDescription: item.purchaseDescription,
            preferredVendor: item.preferredVendor,
            billStatus: item.billStatus
          }));
        logger.log(`Filtered products with billStatus='Available': ${vendorProductList.length} of ${(productRes.data || []).length}`);
        logger.log('Mapped products:', vendorProductList);
      } catch (err) {
        logger.error('Error fetching products:', err);
        logger.warn('No products found:', err.message);
        vendorProductList = [];
      }

      try {
        const serviceRes = await ApiService.get(`/vendor/service-items/company/${companyId}`);
        logger.log('Service API response:', serviceRes);
        logger.log('Service data:', serviceRes.data);
        let filteredServices = (serviceRes.data || [])
          .filter(item => item.billStatus === 'Available'); // Only show items with billStatus = 'Available'
        
        // Filter by vendor if vendorId is provided
        if (vendorId) {
          filteredServices = filteredServices.filter(item => 
            item.preferredVendor && String(item.preferredVendor) === String(vendorId)
          );
          logger.log(`Filtered services for vendor ${vendorId}: ${filteredServices.length} items`);
        }
        
        vendorServiceList = filteredServices.map(item => ({
            id: item.id,
            name: item.serviceName,
            type: item.type || 'Service',
            salesPrice: item.salesPrice,
            salesDescription: item.salesDescription,
            purchasePrice: item.purchasePrice,
            purchaseDescription: item.purchaseDescription,
            preferredVendor: item.preferredVendor,
            billStatus: item.billStatus
          }));
        logger.log(`Filtered services with billStatus='Available': ${vendorServiceList.length} of ${(serviceRes.data || []).length}`);
        logger.log('Mapped services:', vendorServiceList);
      } catch (err) {
        logger.error('Error fetching services:', err);
        logger.warn('No services found:', err.message);
        vendorServiceList = [];
      }

      let vendorInventoryList = [];
      try {
        const inventoryRes = await ApiService.get(`/inventory/companies/${companyId}/items`);
        const inventoryItems = Array.isArray(inventoryRes.data) ? inventoryRes.data : [];
        vendorInventoryList = inventoryItems
          .filter(item => item.active !== false)
          .filter(item => !vendorId || String(item.vendorId || '') === String(vendorId))
          .map(item => ({
            id: item.id,
            name: item.name,
            type: 'Inventory',
            salesPrice: item.salesPrice,
            salesDescription: item.descriptionSales,
            purchasePrice: item.purchasePrice,
            purchaseDescription: item.descriptionPurchase,
            preferredVendor: item.vendorId || null,
            assetsAccountId: item.assetsAccount || null,
            onHandQuantity: item.onHandQuantity
          }));
      } catch (err) {
        logger.error('Error fetching inventory items:', err);
        vendorInventoryList = [];
      }
      
      // Set product/service list filtered by vendor
      this.productServiceList = [...vendorProductList, ...vendorServiceList, ...vendorInventoryList];
      
      logger.log(`Loaded ${vendorProductList.length} products, ${vendorServiceList.length} services and ${vendorInventoryList.length} inventory items for dropdown`);
      logger.log('Final productServiceList:', this.productServiceList);
      
      // Only reset productRows to default empty row if there are no existing rows with data
      // This preserves user selections when vendor auto-selects
      const hasExistingData = this.productRows && this.productRows.some(row => 
        row.itemId || row.selectedItem || row.description || row.quantity > 0 || row.unitPrice > 0
      );
      
      if (!hasExistingData) {
        // Always show default empty row for manual entry (no auto-population)
        this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
      }
    },
    autoPopulateProductServiceRows(vendorProductList, vendorServiceList) {
      // Add safety checks for input parameters
      if (!vendorProductList || !Array.isArray(vendorProductList)) {
        vendorProductList = [];
      }
      if (!vendorServiceList || !Array.isArray(vendorServiceList)) {
        vendorServiceList = [];
      }
      
      // Initialize productRows array if not exists
      if (!this.productRows || !Array.isArray(this.productRows)) {
        this.productRows = [];
      }
      
      // Clear existing product rows
      this.productRows = [];
      
      // Create rows for each vendor-specific product item
      vendorProductList.forEach(product => {
        if (product && product.id && product.type) {
          this.productRows.push({
            itemId: product.id,
            itemType: product.type,
            selectedItem: JSON.stringify({ id: product.id, type: product.type }),
            description: product.name || '',
            quantity: 1, // Default quantity
            unitPrice: product.purchasePrice || product.salesPrice || 0
          });
        }
      });
      
      // Create rows for each vendor-specific service item
      vendorServiceList.forEach(service => {
        if (service && service.id && service.type) {
          this.productRows.push({
            itemId: service.id,
            itemType: service.type,
            selectedItem: JSON.stringify({ id: service.id, type: service.type }),
            description: service.name || '',
            quantity: 1, // Default quantity
            unitPrice: service.purchasePrice || service.salesPrice || 0
          });
        }
      });
      
      logger.log(`Auto-populated ${this.productRows.length} product/service rows for selected vendor`);
    },
    getAvailableItemsForRow(currentRowIndex) {
      // Add safety check for productRows array
      if (!this.productRows || !Array.isArray(this.productRows)) {
        return this.productServiceList || [];
      }
      
      // Add safety check for productServiceList
      if (!this.productServiceList || !Array.isArray(this.productServiceList)) {
        return [];
      }
      
      // Get items that are already selected in other rows (excluding current row)
      const selectedIdsInOtherRows = this.productRows
        .map((row, idx) => {
          if (idx === currentRowIndex) return null; // Skip current row
          if (row && row.itemId && row.itemType && row.selectedItem) {
            try {
              const parsed = JSON.parse(row.selectedItem);
              return `${parsed.type}-${parsed.id}`;
            } catch (e) {
              return null;
            }
          }
          return null;
        })
        .filter(Boolean);
      
      // Filter out items that are already selected in other rows
      const availableItems = this.productServiceList.filter(item => {
        if (!item || !item.type || !item.id) return false;
        const itemKey = `${item.type}-${item.id}`;
        return !selectedIdsInOtherRows.includes(itemKey);
      });
      
      logger.log(`getAvailableItemsForRow(${currentRowIndex}): returning ${availableItems.length} of ${this.productServiceList.length} items`);
      
      return availableItems;
    },
    addAccountRow() {
      this.accountRows.push({ accountId: '', description: '', amount: 0 });
    },
    removeAccountRow(idx) {
      this.accountRows.splice(idx, 1);
    },
    addProductRow() {
      this.productRows.push({ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 });
    },
    removeProductRow(idx) {
      this.productRows.splice(idx, 1);
      // Item becomes available again in other dropdowns - reactivity will handle this
    },
    async onProductServiceChange(row) {
      logger.log('onProductServiceChange called with row:', row);
      logger.log('row.selectedItem:', row.selectedItem);
      logger.log('productServiceList length:', this.productServiceList.length);
      
      if (typeof row.selectedItem === 'string') {
        try {
          const parsed = JSON.parse(row.selectedItem);
          logger.log('Parsed selected item:', parsed);
          row.itemId = parsed.id;
          row.itemType = parsed.type;
        } catch (e) {
          logger.error('Error parsing selected item:', e);
          row.itemId = '';
          row.itemType = '';
        }
      }
      
      logger.log('Looking for item with id:', row.itemId, 'and type:', row.itemType);
      const item = this.productServiceList.find(i => i.id === row.itemId && i.type === row.itemType);
      logger.log('Found item:', item);
      
      if (item) {
        // Auto-fill description with purchaseDescription if available, otherwise use name
        const descriptionToUse = item.purchaseDescription || item.name || '';
        row.description = descriptionToUse;
        logger.log('Description source - purchaseDescription:', item.purchaseDescription, 'name:', item.name, 'using:', descriptionToUse);
        logger.log('Setting description to:', row.description);
        
        // Auto-fill unit price - Always use purchasePrice first, fall back to salesPrice
        const priceToUse = (item.purchasePrice && item.purchasePrice > 0) 
          ? item.purchasePrice 
          : (item.salesPrice && item.salesPrice > 0 ? item.salesPrice : 0);
        row.unitPrice = Number(priceToUse) || 0;
        logger.log('Setting unit price to:', row.unitPrice, 'from purchasePrice:', item.purchasePrice, 'or salesPrice:', item.salesPrice);
        
        // Set default quantity to 1 if not already set (so account row appears immediately)
        if (!row.quantity || row.quantity === 0) {
          row.quantity = 1;
        }

        if (item.type === 'Inventory' && !item.assetsAccountId) {
          this.showAlertModal(
            'Assets Account Missing',
            `"${item.name}" has no Assets Account. Set one on the inventory item so this purchase is added to the bill total.`
          );
        }
        
        // Auto-select vendor based on product/service's preferredVendor
        // Only auto-select if vendor is not already set to this preferred vendor (to avoid unnecessary watcher trigger)
        if (item.preferredVendor) {
          const preferredVendorIdStr = String(item.preferredVendor);
          const currentVendorIdStr = String(this.vendorId || '');
          
          // Only change vendor if it's different from current vendor
          if (preferredVendorIdStr !== currentVendorIdStr) {
            const preferredVendor = this.vendorList.find(v => 
              String(v.id) === preferredVendorIdStr
            );
            
            if (preferredVendor) {
              // Auto-select the preferred vendor
              this.vendorId = preferredVendorIdStr;
              logger.log(`Auto-selected vendor ${preferredVendor.vendorName} based on ${row.itemType}'s preferredVendor`);
              // Wait for vendor watcher to start (it will fetch expense accounts asynchronously)
              await this.$nextTick();
              // Give the vendor watcher a moment to start fetching expense accounts
              await new Promise(resolve => setTimeout(resolve, 100));
            } else {
              logger.warn(`${row.itemType} has preferredVendor ${item.preferredVendor} but vendor not found in vendorList`);
            }
          }
        }
        
        // If vendor exists, ensure expense accounts are refreshed
        // This ensures the expense account mapping for this item is available
        if (this.vendorId) {
          logger.log('Refreshing expense accounts for selected item');
          await this.fetchExpenseAccounts(this.vendorId);
          await this.$nextTick();
        }
      } else {
        logger.warn('Item not found in productServiceList');
        row.description = '';
        row.unitPrice = 0;
      }
    },
    updateDueDate() {
      if (this.loadingExistingBill) return;
      const selectedTerm = this.paymentTermsList.find(term => String(term.id) === String(this.paymentTerm));
      if (selectedTerm && this.billDate) {
        const dueDays = Number(selectedTerm.dueDays) || 0;
        const billDateObj = this.parseDateFromDatePicker(this.billDate);
        billDateObj.setDate(billDateObj.getDate() + dueDays);
        this.dueDate = this.formatDateForDatePicker(billDateObj);
      }
    },
    formatDateForDatePicker(date) {
      if (!date) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    parseIsoDate(value) {
      if (!value) return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
      const text = String(value);
      const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      const parsed = new Date(text);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    async hoursRowFromConsultantRecord(row) {
      const consultantId = row.consultantId ? String(row.consultantId) : '';
      const timesheetId = row.timesheetId || null;
      if (consultantId) {
        await this.ensureConsultantInCompanyList(consultantId);
      }
      let availableTimesheets = [];
      if (consultantId) {
        try {
          availableTimesheets = await this.getBillableTimesheetsForConsultant(consultantId, {
            specificTimesheetId: timesheetId || null,
          });
        } catch (error) {
          logger.warn('Could not load timesheets for draft consultant:', error);
        }
      }
      const timesheetData = timesheetId
        ? availableTimesheets.find((ts) => String(ts.id) === String(timesheetId)) || null
        : null;
      if (timesheetId && !timesheetData) {
        availableTimesheets = [
          {
            id: timesheetId,
            hours: Number(row.hours) || 0,
            fromDate: row.fromDate || row.from_date || '',
            toDate: row.toDate || row.to_date || '',
          },
          ...availableTimesheets,
        ];
      }
      return {
        id: consultantId,
        description: row.description || (timesheetData ? this.buildTimesheetDescription(timesheetData) : ''),
        hours: Number(row.hours) || 0,
        hourlyRate: Number(row.hourlyRate) || 0,
        timesheetId,
        timesheetData: timesheetData || availableTimesheets.find((ts) => String(ts.id) === String(timesheetId)) || null,
        availableTimesheets,
        selectedTimesheetId: timesheetId ? String(timesheetId) : '',
      };
    },
    async mergeRelatedCapturedBills(primaryBillId) {
      const ids = String(this.$route.query.relatedBillIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id && id !== String(primaryBillId));
      if (!ids.length || !this.isConsultantIndustry) return;

      const seenTimesheets = new Set(
        (this.consultantHoursRows || [])
          .map((row) => (row.timesheetId != null ? String(row.timesheetId) : ''))
          .filter(Boolean)
      );
      const extraRows = [];
      for (const id of ids) {
        try {
          const bill = await ApiService.getEnterBillById(id);
          const consultantRows = bill?.consultantTimesheetRecords || bill?.consultantRecords || bill?.consultants || [];
          for (const row of consultantRows) {
            const timesheetKey = row.timesheetId != null ? String(row.timesheetId) : '';
            if (timesheetKey && seenTimesheets.has(timesheetKey)) continue;
            const hoursRow = await this.hoursRowFromConsultantRecord(row);
            if (timesheetKey) seenTimesheets.add(timesheetKey);
            if (hoursRow.id || hoursRow.hours) extraRows.push(hoursRow);
          }
        } catch (error) {
          logger.warn('Could not load a related draft for this captured bill:', id, error);
        }
      }
      if (!extraRows.length) return;
      const current = (this.consultantHoursRows || []).filter((row) => row.id || row.hours);
      this.consultantHoursRows = current.concat(extraRows);
      this.activeTab = 'consultant';
    },
    async loadDraftBillForAdjust(billId) {
      try {
        const bill = await ApiService.getEnterBillById(billId);
        if (!bill?.id) return;
        this.editingBillId = bill.id;
        this.savedBillId = bill.id;
        this.vendorId = String(bill.vendorId || this.vendorId || '');
        this.contactName = bill.contactName || this.contactName;
        this.phone = bill.phone || this.phone;
        if (bill.paymentTerm) {
          this.paymentTerm = String(bill.paymentTerm);
          this.previousPaymentTerm = String(bill.paymentTerm);
        }
        this.invNo = bill.billNo || this.invNo || '';
        this.replaceDraftBillNo = this.isAutoDraftBillNo(this.invNo);
        const billDate = this.parseIsoDate(bill.date);
        if (billDate) {
          this.billDate = this.formatDateForDatePicker(billDate);
          this.deliveryDate = this.billDate;
        }
        const dueDate = this.parseIsoDate(bill.dueDate);
        if (dueDate) this.dueDate = this.formatDateForDatePicker(dueDate);
        this.comments = bill.comments || '';
        this.attachments = Array.isArray(bill.attachments) ? bill.attachments : [];

        const consultantRows = bill.consultantTimesheetRecords || bill.consultantRecords || bill.consultants || [];
        if (consultantRows.length && this.isConsultantIndustry) {
          this.activeTab = 'consultant';
          const hoursRows = [];
          for (const row of consultantRows) {
            hoursRows.push(await this.hoursRowFromConsultantRecord(row));
          }
          this.consultantHoursRows = hoursRows;
        }

        const itemRows = bill.itemRecords || bill.items || [];
        if (itemRows.length) {
          this.productRows = itemRows.map((row) => ({
            itemId: row.itemId,
            itemType: row.itemType || '',
            selectedItem: row.itemType
              ? JSON.stringify({ id: row.itemId, type: row.itemType })
              : row.itemId,
            description: row.description || '',
            quantity: Number(row.quantity) || 0,
            unitPrice: Number(row.unitPrice) || 0,
          }));
          if (!consultantRows.length) this.activeTab = 'product';
        }

        await this.mergeRelatedCapturedBills(bill.id);

        this.originalDraftSnapshot = this.snapshotDraftForHistory();
        await this.loadCapturedReviewForAdjust();
        this.applyCapturedReviewToDraft();
        await this.ensureCapturedMailAttachments();
        if (this.vendorId) {
          await this.populateVendorDefaults(this.vendorId, { overwriteHeader: false });
          await this.ensureVendorBillsLoaded(this.vendorId);
          // Auto drafts often have no paymentTerm saved — use vendor preference
          if (!this.paymentTerm) {
            await this.applyVendorPreferredPaymentTerm(this.vendorId);
          }
        }
        this.applyDefaultVendorContact();
        if (this.$route.query.fromCaptured || this.capturedReview) {
          this.replaceDraftBillNo = false;
          this.applyCapturedBillNumber();
          this.comments = this.buildCapturedBillAuditComment({ posted: false });
        } else if (this.replaceDraftBillNo) {
          // Place draft: replace DRAFT-… placeholder with vendor + due-date bill no
          await this.generateInvNo({ force: true });
        }
      } catch (error) {
        logger.error('Failed to load draft bill for review:', error);
        this.showAlertModal(
          'Could not load draft',
          error.response?.data?.error || error.message || 'Could not load this draft bill.'
        );
      }
    },
    async loadCapturedReviewForAdjust() {
      const messageId = this.$route.query.capturedMessageId;
      const companyId = this.$route.params.id;
      if (!messageId || !companyId) return;
      try {
        const url = `${process.env.VUE_APP_API_URL}/outlook/bill-mail/reviews?companyId=${encodeURIComponent(companyId)}&messageIds=${encodeURIComponent(messageId)}`;
        const response = await fetch(url);
        const result = await response.json().catch(() => ({}));
        const rows = Array.isArray(result.data) ? result.data : [];
        this.capturedReview = rows.find((row) => String(row.messageId) === String(messageId)) || null;
      } catch (error) {
        logger.warn('Could not load captured mail review for draft adjust:', error);
      }
    },
    capturedAttachmentNames() {
      const review = this.capturedReview || {};
      const extractNames = (Array.isArray(review.extracts) ? review.extracts : [])
        .map((item) => String(item?.name || '').trim())
        .filter(Boolean);
      const metaNames = (Array.isArray(review.attachments) ? review.attachments : [])
        .map((item) => String(item?.name || item?.fileName || '').trim())
        .filter(Boolean);
      const names = extractNames.length ? extractNames : metaNames;
      return new Set(names.map((name) => name.toLowerCase()));
    },
    withCompanyQuery(url) {
      const companyId = this.$route.params.id;
      if (!companyId) return url;
      const join = url.includes('?') ? '&' : '?';
      return `${url}${join}companyId=${encodeURIComponent(companyId)}`;
    },
    blobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    async listCapturedMailAttachmentMeta() {
      const review = this.capturedReview || {};
      const fromReview = Array.isArray(review.attachments) ? review.attachments : [];
      const list = fromReview
        .filter((item) => item && (item.id || item.filePath))
        .map((item) => ({
          id: item.id,
          name: item.name || item.fileName || 'attachment',
          contentType: item.contentType || item.mimeType || 'application/octet-stream',
          size: item.size || item.fileSize || 0,
          filePath: item.filePath || null,
        }));
      if (list.length) return list;

      const messageId = this.$route.query.capturedMessageId;
      if (!messageId) return [];
      const service = String(review.mailService || 'outlook').toLowerCase() === 'gmail' ? 'gmail' : 'outlook';
      const base = service === 'gmail' ? '/gmail/read' : '/outlook/read';
      let url = `${process.env.VUE_APP_API_URL}${base}/${encodeURIComponent(messageId)}`;
      if (service === 'outlook') url = this.withCompanyQuery(url);
      const response = await fetch(url);
      const result = await response.json().catch(() => ({}));
      const email = result.data || result;
      const attachments = Array.isArray(email?.attachments) ? email.attachments : [];
      return attachments
        .filter((item) => item && item.id)
        .map((item) => ({
          id: item.id,
          name: item.name || item.fileName || 'attachment',
          contentType: item.contentType || item.mimeType || 'application/octet-stream',
          size: item.size || item.fileSize || 0,
          filePath: null,
        }));
    },
    async fetchCapturedMailAttachmentBlob(meta) {
      if (meta?.filePath && !String(meta.filePath).startsWith('data:')) {
        const filename = String(meta.filePath).includes('/')
          ? String(meta.filePath).split('/').pop()
          : meta.filePath;
        const url = this.withCompanyQuery(
          `${process.env.VUE_APP_API_URL}/vendor/documents/${encodeURIComponent(filename)}`
        );
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to open saved bill attachment');
        return response.blob();
      }
      const messageId = this.$route.query.capturedMessageId;
      if (!messageId || !meta?.id) throw new Error('Attachment is not available');
      const service = String(this.capturedReview?.mailService || 'outlook').toLowerCase() === 'gmail'
        ? 'gmail'
        : 'outlook';
      const base = service === 'gmail' ? '/gmail/read' : '/outlook/read';
      let url = `${process.env.VUE_APP_API_URL}${base}/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(meta.id)}`;
      if (service === 'gmail') {
        url += `?filename=${encodeURIComponent(meta.name || 'attachment')}&contentType=${encodeURIComponent(meta.contentType || 'application/octet-stream')}`;
      } else {
        url = this.withCompanyQuery(url);
      }
      const response = await fetch(url);
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Failed to download attachment');
      }
      return response.blob();
    },
    async ensureCapturedMailAttachments() {
      if (!this.$route.query.fromCaptured && !this.capturedReview) return;
      if ((this.attachments || []).length) return;
      try {
        const metas = await this.listCapturedMailAttachmentMeta();
        if (!metas.length) return;
        const loaded = [];
        for (const meta of metas.slice(0, 4)) {
          try {
            const blob = await this.fetchCapturedMailAttachmentBlob(meta);
            const filePath = await this.blobToDataUrl(blob);
            loaded.push({
              fileName: meta.name || 'attachment',
              fileSize: meta.size || blob.size || 0,
              filePath,
              mimeType: meta.contentType || blob.type || 'application/octet-stream',
              source: 'captured-mail',
            });
          } catch (error) {
            logger.warn('Could not load captured mail attachment:', meta.name, error);
          }
        }
        if (loaded.length) this.attachments = loaded;
      } catch (error) {
        logger.warn('Could not load captured mail attachments onto Create Bill:', error);
      }
    },
    applyCapturedBillNumber() {
      const serial = String(this.capturedInvoiceNo || '').replace(/^#/, '').trim();
      if (!serial) return;
      this.invNo = this.formatVendorBillNo(serial);
    },
    formatVendorBillNo(suffix) {
      const vendor =
        this.selectedVendor?.vendorName ||
        this.selectedVendor?.name ||
        this.$route.query.vendorName ||
        '';
      const dateSource = this.dueDate || this.billDate;
      let datePart = '';
      if (dateSource) {
        const parsed = this.parseDateFromDatePicker(dateSource);
        if (parsed && !Number.isNaN(parsed.getTime())) {
          const yyyy = parsed.getFullYear();
          const mm = String(parsed.getMonth() + 1).padStart(2, '0');
          const dd = String(parsed.getDate()).padStart(2, '0');
          datePart = `${yyyy}${mm}${dd}`;
        }
      }
      const serial = String(suffix || '').replace(/^#/, '').trim();
      const parts = [];
      if (vendor) parts.push(String(vendor).trim());
      parts.push('BILL');
      if (datePart) parts.push(datePart);
      if (serial) parts.push(serial);
      return parts.join('-');
    },
    applyCapturedReviewToDraft() {
      if (!this.$route.query.fromCaptured && !this.capturedReview) return;
      const review = this.capturedReview;
      const fields = extractCapturedBillFields(review?.summary || '');

      if (fields.invoiceNumber) {
        this.capturedInvoiceNo = String(fields.invoiceNumber).replace(/^#/, '').trim();
      } else {
        const fromComments = String(this.comments || '').match(/from invoice\s+(.+)$/i);
        if (fromComments && !this.capturedInvoiceNo) {
          this.capturedInvoiceNo = fromComments[1].trim();
        }
      }
      if (fields.invoiceDate) {
        this.billDate = this.formatDateForDatePicker(fields.invoiceDate);
        this.deliveryDate = this.billDate;
      }

      const matchedTerm = this.matchPaymentTermFromCaptured(fields.paymentTermsText);
      if (matchedTerm) {
        this.paymentTerm = String(matchedTerm.id);
        this.previousPaymentTerm = String(matchedTerm.id);
      }

      if (fields.dueDate) {
        this.dueDate = this.formatDateForDatePicker(fields.dueDate);
      } else if (matchedTerm && this.billDate) {
        const dueDays = Number(matchedTerm.dueDays) || 0;
        const billDateObj = this.parseDateFromDatePicker(this.billDate);
        billDateObj.setDate(billDateObj.getDate() + dueDays);
        this.dueDate = this.formatDateForDatePicker(billDateObj);
      }

      this.applyCapturedBillNumber();

      const billAttachments = Array.isArray(this.attachments) ? this.attachments : [];
      const capturedNames = this.capturedAttachmentNames();
      const capturedMailFiles = billAttachments.filter(
        (att) => String(att.source || '') === 'captured-mail'
      );
      if (capturedNames.size) {
        const matched = billAttachments.filter((att) =>
          capturedNames.has(String(att.fileName || att.name || '').trim().toLowerCase())
        );
        if (matched.length) {
          this.attachments = matched;
        } else if (capturedMailFiles.length) {
          this.attachments = capturedMailFiles;
        }
      } else if (capturedMailFiles.length) {
        this.attachments = capturedMailFiles;
      }

      this.consultantHoursRows = (this.consultantHoursRows || []).map((row) => {
        const data = row.timesheetData;
        if (!data) return row;
        return {
          ...row,
          timesheetData: { ...data, attachments: [] },
        };
      });
    },
    matchPaymentTermFromCaptured(text) {
      const blob = String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!blob) return null;
      const terms = Array.isArray(this.paymentTermsList) ? this.paymentTermsList : [];
      if (!terms.length) return null;

      const net = blob.match(/net\s*(\d+)/i);
      if (net) {
        const days = Number(net[1]);
        const byDays = terms.find((term) => Number(term.dueDays) === days);
        if (byDays) return byDays;
        const byName = terms.find((term) =>
          new RegExp(`\\bnet\\s*${days}\\b`, 'i').test(`${term.name || ''} ${term.description || ''}`)
        );
        if (byName) return byName;
      }

      if (/due\s*on\s*receipt|upon\s*receipt|immediate/i.test(blob)) {
        const receipt = terms.find((term) =>
          /receipt|immediate|due\s*now/i.test(`${term.name || ''} ${term.description || ''}`)
          || Number(term.dueDays) === 0
        );
        if (receipt) return receipt;
      }

      return terms.find((term) => {
        const name = String(term.name || '').trim().toLowerCase();
        return name && name.length > 1 && blob.includes(name);
      }) || null;
    },
    originalDraftInvoiceNo(comments = this.comments) {
      const text = String(comments || '');
      const auto = text.match(/from invoice\s+(\S+)/i);
      if (auto) return auto[1].replace(/[.,;]+$/, '').trim();
      const audit = text.match(/auto-created from invoice\s+(\S+)/i);
      if (audit) return audit[1].replace(/[.,;]+$/, '').trim();
      return '';
    },
    extraUserBillComments(comments = this.comments) {
      return String(comments || '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => {
          if (!line) return false;
          if (/^auto-created draft bill from invoice/i.test(line)) return false;
          if (/^draft bill was auto-created/i.test(line)) return false;
          if (/^matched to captured vendor mail/i.test(line)) return false;
          if (/^this record will be posted as an open bill/i.test(line)) return false;
          if (/^posted as open bill/i.test(line)) return false;
          if (/^(vendor|contact|mail from|mail subject|mail received|invoice \/ bill no|bill date|payment terms|due date|amount|attachment|consultant hours)\s*:/i.test(line)) {
            return false;
          }
          return true;
        })
        .join('\n')
        .trim();
    },
    paymentTermAuditLabel() {
      const term = (this.paymentTermsList || []).find(
        (item) => String(item.id) === String(this.paymentTerm)
      );
      if (!term) return '';
      return term.description ? `${term.name} (${term.description})` : String(term.name || '');
    },
    formatMailReceivedForAudit(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
    buildCapturedBillAuditComment({ posted = false } = {}) {
      const review = this.capturedReview || {};
      const originalInvoice = this.originalDraftInvoiceNo() || this.invNo || 'unknown';
      const vendor =
        this.selectedVendor?.vendorName ||
        this.selectedVendor?.name ||
        this.$route.query.vendorName ||
        'Vendor';
      const fromName = review.fromName || '';
      const fromAddress = review.fromAddress || '';
      const mailFrom = fromName && fromAddress
        ? `${fromName} <${fromAddress}>`
        : fromName || fromAddress;
      const amount = this.capturedReviewTotalLabel
        || (Number.isFinite(Number(this.finalTotal))
          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(this.finalTotal))
          : '');
      const attachmentNames = (this.attachments || [])
        .map((att) => att.fileName || att.name)
        .filter(Boolean);
      const hoursRows = (this.consultantHoursRows || [])
        .filter((row) => row.id && (row.hours || row.description))
        .map((row) => {
          const name = (this.allCompanyConsultants || []).find(
            (c) => String(c.id) === String(row.id)
          );
          const consultantName = name?.consultantName || name?.name || 'Consultant';
          const hours = Number(row.hours) || 0;
          const rate = Number(row.hourlyRate) || 0;
          return `${consultantName}: ${hours} hrs @ $${rate}/hr (${row.description || 'no period'})`;
        });
      const today = this.formatDateForDatePicker(new Date());
      const lines = [
        `Draft bill was auto-created from invoice ${originalInvoice}.`,
        `Matched to captured vendor mail and updated to the actual invoice on ${today}.`,
        `Vendor: ${vendor}`,
        this.contactName ? `Contact: ${this.contactName}${this.phone ? ` (${this.phone})` : ''}` : '',
        mailFrom ? `Mail from: ${mailFrom}` : '',
        review.subject ? `Mail subject: ${review.subject}` : '',
        review.receivedDateTime ? `Mail received: ${this.formatMailReceivedForAudit(review.receivedDateTime)}` : '',
        this.invNo ? `Invoice / bill no: ${this.invNo}` : '',
        this.billDate ? `Bill date: ${this.billDate}` : '',
        this.paymentTermAuditLabel() ? `Payment terms: ${this.paymentTermAuditLabel()}` : '',
        this.dueDate ? `Due date: ${this.dueDate}` : '',
        amount ? `Amount: ${amount}` : '',
        attachmentNames.length ? `Attachment: ${attachmentNames.join(', ')}` : '',
        hoursRows.length ? `Consultant hours: ${hoursRows.join('; ')}` : '',
        posted
          ? `Posted as Open bill on ${today} from captured-mail review (Create Bill).`
          : 'This record will be posted as an Open bill when saved from Create Bill.',
      ].filter(Boolean);

      const extra = this.extraUserBillComments();
      if (extra) lines.push(extra);
      return lines.join('\n');
    },
    finalizeCapturedBillAuditComment() {
      this.comments = this.buildCapturedBillAuditComment({ posted: true });
    },
    parseDateFromDatePicker(dateString) {
      if (!dateString || !dateString.includes('/')) return new Date();
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
      return new Date();
    },
    async generateInvNo({ force = false } = {}) {
      // Keep saved bill nos when editing, except when placing an auto-draft (DRAFT-…) bill
      if (!force && (this.loadingExistingBill || this.editingBillId) && !this.replaceDraftBillNo) {
        return;
      }
      const vendor = this.selectedVendor ? this.selectedVendor.vendorName || this.selectedVendor.name || '' : '';
      let dueDate = this.dueDate || '';
      if (dueDate) {
        const parsedDate = this.parseDateFromDatePicker(dueDate);
        const yyyy = parsedDate.getFullYear();
        const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(parsedDate.getDate()).padStart(2, '0');
        dueDate = `${yyyy}${mm}${dd}`;
      }
      
      // Get the next serial number based on vendor's previous bills
      const nextSerial = await this.getNextSerialNumber();
      const serial = String(nextSerial).padStart(3, '0');
      
      let parts = [];
      if (vendor) parts.push(vendor);
      parts.push('BILL');
      if (dueDate) parts.push(dueDate);
      parts.push(serial);
      this.invNo = parts.join('-');
    },
    isAutoDraftBillNo(billNo) {
      return /^DRAFT-/i.test(String(billNo || '').trim());
    },
    async ensureVendorBillsLoaded(vendorId = this.vendorId) {
      if (!vendorId) {
        this.vendorBills = [];
        return;
      }
      if (Array.isArray(this.vendorBills) && this.vendorBills.length) return;
      try {
        const bills = await ApiService.getVendorBills(vendorId);
        this.vendorBills = Array.isArray(bills) ? bills : [];
      } catch (err) {
        logger.error('Error fetching vendor bills:', err);
        this.vendorBills = [];
      }
    },
    async getNextSerialNumber() {
      if (!this.vendorId || this.vendorBills.length === 0) {
        return this.serialNo;
      }
      
      // Extract serial numbers from existing bill numbers for this vendor
      const vendorName = this.selectedVendor ? this.selectedVendor.vendorName || this.selectedVendor.name || '' : '';
      const billNumbers = this.vendorBills
        .map(bill => bill.billNo || bill.invNo || '')
        .filter(billNo => billNo.includes(vendorName) && billNo.includes('BILL'))
        .map(billNo => {
          // Extract serial number from bill number format: VENDOR-BILL-N{days}-{date}-{serial}
          const parts = billNo.split('-');
          const lastPart = parts[parts.length - 1];
          return parseInt(lastPart, 10) || 0;
        })
        .filter(serial => !isNaN(serial));
      
      if (billNumbers.length === 0) {
        return this.serialNo;
      }
      
      // Find the highest serial number and increment it
      const maxSerial = Math.max(...billNumbers);
      return maxSerial + 1;
    },
    async validatePayFromBalance() {
      this.payFromBalanceError = '';
      if (!this.payFrom) return;
      try {
        const res = await ApiService.get(`/company-account/${this.payFrom}`);
        const account = res.data.account || res.data;
        const total = this.accountTotal + this.productServiceTotal;
        if (account && Number(account.currentBalance) < total) {
          this.payFromBalanceError = 'Insufficient balance in the selected account.';
        }
      } catch (e) {
        this.payFromBalanceError = 'Unable to check account balance.';
      }
    },
    async handleSaveAndClose() {
      // Validate required fields first
      const validationErrors = this.validateRequiredFields();
      if (validationErrors.length > 0) {
        this.showValidationError(validationErrors);
        return;
      }

      await this.validatePayFromBalance();
      if (this.payFromBalanceError) return;

      this.beginCreateBillPipeline({
        pendingAction: 'saveAndCloseToPayBill',
        savedInvNo: this.invNo,
        journalEntry: null,
        journalSkipped: false,
        journalFailed: false,
        journalFailureDetail: ''
      });
      this.updateCreateBillProgressorStep('bill', { status: 'active' });

      try {
        await this.saveBill();
        await this.runCompanyBillNotificationsAfterSave();
        this.updateCreateBillProgressorStep('bill', { status: 'done', detail: 'Saved to server.' });
        this.updateCreateBillProgressorStep('journal', { status: 'active' });
        this.journalAutoCreateActive = true;
      } catch (err) {
        this.abortCreateBillPipeline();
        this.showBillSaveAlert(err);
      }
    },
    async handleSaveAndNew() {
      // Validate required fields first
      const validationErrors = this.validateRequiredFields();
      if (validationErrors.length > 0) {
        this.showValidationError(validationErrors);
        return;
      }

      await this.validatePayFromBalance();
      if (this.payFromBalanceError) return;

      this.beginCreateBillPipeline({
        pendingAction: 'saveAndNew',
        savedInvNo: this.invNo,
        journalEntry: null,
        journalSkipped: false,
        journalFailed: false,
        journalFailureDetail: ''
      });
      this.updateCreateBillProgressorStep('bill', { status: 'active' });

      try {
        await this.saveBill();
        await this.runCompanyBillNotificationsAfterSave();
        this.updateCreateBillProgressorStep('bill', { status: 'done', detail: 'Saved to server.' });
        this.updateCreateBillProgressorStep('journal', { status: 'active' });
        this.journalAutoCreateActive = true;
      } catch (err) {
        this.abortCreateBillPipeline();
        this.showBillSaveAlert(err);
      }
    },
    async fetchAllConsultantsForCompany(vendorId = null) {
      try {
        const companyId = this.$route.params.id;
        let consultants = [];
        
        // Always fetch all consultants for the company first
        consultants = await ApiService.getAllConsultants(companyId);
        consultants = Array.isArray(consultants) ? consultants : [];
        logger.log('📊 Fetched all consultants for company:', consultants.length);
        
        // If vendorId is provided, filter consultants based on can_work_multiple_customers
        if (vendorId) {
          // Step 1: Separate consultants by can_work_multiple_customers
          const multiCustomerConsultants = consultants.filter(c => c.can_work_multiple_customers === true);
          const singleCustomerConsultants = consultants.filter(c => !c.can_work_multiple_customers || c.can_work_multiple_customers === false);
          
          logger.log('Multi-customer consultants:', multiCustomerConsultants.length);
          logger.log('Single-customer consultants:', singleCustomerConsultants.length);
          
          // Step 2: For multi-customer consultants, get ConsultantCustomerService records
          let multiCustomerConsultantIds = [];
          if (multiCustomerConsultants.length > 0) {
            try {
              const serviceUrl = companyId 
                ? `/customer/consultant-customer-services?preferredVendorId=${vendorId}&companyId=${companyId}`
                : `/customer/consultant-customer-services?preferredVendorId=${vendorId}`;
              
              logger.log('Fetching ConsultantCustomerService records for vendor:', vendorId);
              const serviceResponse = await ApiService.get(serviceUrl);
              const services = Array.isArray(serviceResponse.data) ? serviceResponse.data : [];
              
              logger.log('Found ConsultantCustomerService records:', services.length);
              
              // Get consultant IDs from services
              multiCustomerConsultantIds = services.map(s => String(s.consultantId));
              logger.log('Consultant IDs from ConsultantCustomerService:', multiCustomerConsultantIds);
            } catch (error) {
              logger.error('Error fetching ConsultantCustomerService records:', error);
            }
          }
          
          // Step 3: Filter consultants
          // For multi-customer: include if consultantId is in the service records
          // For single-customer: include if preferredVendorId matches
          const filteredMultiCustomer = multiCustomerConsultants.filter(c => 
            multiCustomerConsultantIds.includes(String(c.id))
          );
          
          const filteredSingleCustomer = singleCustomerConsultants.filter(c => 
            c.preferredVendorId && String(c.preferredVendorId) === String(vendorId) && c.buyService === true
          );
          
          // Combine both groups
          consultants = [...filteredMultiCustomer, ...filteredSingleCustomer];
          logger.log('Filtered consultants for vendor:', consultants.length, 'consultants');
          logger.log('  - Multi-customer:', filteredMultiCustomer.length);
          logger.log('  - Single-customer:', filteredSingleCustomer.length);
        }
        
        const filterVendorId = vendorId != null && vendorId !== '' ? String(vendorId) : null;
        if (filterVendorId != null) {
          if (String(this.vendorId) !== filterVendorId) {
            logger.log('Skipping stale fetchAllConsultantsForCompany result for vendor', filterVendorId);
            return;
          }
        } else if (this.vendorId != null && this.vendorId !== '') {
          logger.log('Skipping company-wide fetchAllConsultantsForCompany — vendor already selected');
          return;
        }
        
        this.allCompanyConsultants = consultants;
        logger.log('📊 C2C Consultants:', this.allCompanyConsultants.filter(c => c.consultantType === 'C2C'));
      } catch (error) {
        logger.error('Error fetching consultants:', error);
        this.allCompanyConsultants = [];
      }
    },
    async fetchConsultantsForVendor(vendorId) {
      if (!this.isConsultantIndustry) {
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        return;
      }
      if (!vendorId) {
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        // Always keep consultant section visible
        return;
      }
      
      this.consultantsLoading = true;
      this.consultantsError = '';
      
      try {
        // First check if this vendor has any consultants
        const consultantsRes = await ApiService.get(`/consultant/by-vendor/${vendorId}`);
        const consultants = Array.isArray(consultantsRes.data.data) ? consultantsRes.data.data : [];
        
        if (consultants.length === 0) {
          // Always keep consultant section visible
          this.consultantTimesheets = [];
          this.consultantOptions = [];
          this.allConsultantOptions = [];
          return;
        }
        
        this.showConsultantTimesheets = true;
        
        // Get timesheets for these consultants
        // Note: Filter to only show timesheets with billStatus = 'Not Applicable'
        // This ensures only timesheets not yet added to any bill are available
        const draftLinkedIds = await this.loadDraftLinkedTimesheetIds({ force: true });
        const draftLinkedSet = new Set((draftLinkedIds || []).map((id) => String(id)));
        let consultantOptionsList = [];
        
        for (const consultant of consultants) {
          try {
            const timesheetsRes = await ApiService.get(`/consultant/consultants-with-timesheets?consultantId=${consultant.id}`);
            const consultantsWithTimeSheets = Array.isArray(timesheetsRes.data.data) ? timesheetsRes.data.data : [];
            
            consultantsWithTimeSheets.forEach(c => {
              if (c.timeSheets && c.timeSheets.length > 0) {
                c.timeSheets.forEach(timesheet => {
                  // Only show timesheets with billStatus = 'Not Applicable' (not yet added to any bill)
                  if (timesheet.billStatus !== 'Not Applicable') {
                    return; // Skip this timesheet (either bill created, paid, or null)
                  }
                  // Vendor-portal submissions belong under Draft Bills → Submitted
                  if (timesheet.isVendorInvoice === true || timesheet.isVendorInvoice === 'true') {
                    return;
                  }
                  // Invoice/portal draft bills already own this timesheet — open that draft instead
                  if (draftLinkedSet.has(String(timesheet.id))) {
                    return;
                  }
                  
                  const option = {
                    id: c.id,
                    name: c.name || c.consultantName || '',
                    description: this.buildTimesheetDescription(timesheet),
                    hourlyRate: Number(consultant.purchasePrice) || Number(c.hourlyRate) || 0,
                    hours: Number(timesheet.hours) || 0,
                    timesheetId: timesheet.id,
                    attachment: timesheet.attachment || null,
                    timeSheets: [timesheet]
                  };
                  consultantOptionsList.push(option);
                });
              }
            });
          } catch (err) {
            logger.error(`Error fetching timesheets for consultant ${consultant.id}:`, err);
          }
        }
        
        // Store all options for manual selection (no auto-population)
        this.allConsultantOptions = [...consultantOptionsList];
        this.consultantOptions = consultantOptionsList;
        
        // Don't auto-add consultant timesheets - let user select manually
        this.consultantTimesheets = [];
        
        // Consultant timesheets are stored but no notification is shown
        // Users can manually add consultants in the Itemize by Consultant Hours section
        
        // Note: Expense accounts are now fetched independently when vendor is selected
        
      } catch (err) {
        this.consultantsError = err.response?.data?.error || err.message || 'Failed to fetch consultant timesheets';
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        // Always keep consultant section visible
        this.showAlertModal('Could not load consultants', this.consultantsError);
      } finally {
        this.consultantsLoading = false;
      }
    },
    /** Match vendor consultant expense mapping (optionally per service agreement). */
    findConsultantExpenseMapping(consultantId, consultantCustomerServiceId = null) {
      if (consultantId == null || consultantId === '') return null;
      const want = String(consultantId);
      const serviceWant =
        consultantCustomerServiceId != null && consultantCustomerServiceId !== ''
          ? String(consultantCustomerServiceId)
          : null;

      if (serviceWant) {
        const byService = this.expenseAccountMappings.find(
          (m) =>
            m.type === 'consultant' &&
            String(m.consultantId) === want &&
            m.consultantCustomerServiceId != null &&
            String(m.consultantCustomerServiceId) === serviceWant
        );
        if (byService) return byService;
      }

      return (
        this.expenseAccountMappings.find(
          (m) =>
            m.type === 'consultant' &&
            String(m.consultantId) === want &&
            (m.consultantCustomerServiceId == null || m.consultantCustomerServiceId === '')
        ) || null
      );
    },
    /** Resolve expense account from mappings or timesheet service agreement data. */
    resolveConsultantExpenseMapping(consultantId, context = {}) {
      const serviceId =
        context.consultantCustomerServiceId ||
        context.consultantCustomerService?.id ||
        null;
      let mapping = this.findConsultantExpenseMapping(consultantId, serviceId);
      const ccs = context.consultantCustomerService;
      if (!mapping && ccs?.expenseAccountId) {
        mapping = {
          type: 'consultant',
          consultantId,
          consultantCustomerServiceId: serviceId,
          accountId: ccs.expenseAccountId
        };
      }
      return mapping;
    },
    async fetchExpenseAccounts(vendorId) {
      try {
        const companyId = this.$route.params.id;
        let allAccountMappings = [];

        // Fetch consultant expense accounts from the new API
        try {
          const accountsRes = await ApiService.get(`/consultant/consultants-expense-accounts/${vendorId}`);
          const consultantsWithExpenseAccounts = Array.isArray(accountsRes.data.data) ? accountsRes.data.data : [];
          logger.log(`Consultant expense accounts for vendor ${vendorId}:`, consultantsWithExpenseAccounts);
          
          // Add consultant mappings
          consultantsWithExpenseAccounts.forEach(consultant => {
            if (consultant.expenseAccountId) {
              allAccountMappings.push({
                type: 'consultant',
                accountId: consultant.expenseAccountId,
                description: `Consultant Expense - ${consultant.consultantName}`,
                itemName: consultant.consultantName,
                consultantId: consultant.consultantId,
                consultantCustomerServiceId: consultant.consultantCustomerServiceId || null
              });
            }
          });
        } catch (err) {
          logger.error('Error fetching consultant expense accounts:', err);
        }

        // Fetch product items expense accounts from the new API
        try {
          const productRes = await ApiService.get(`/vendor/product-items/product-items-expense-accounts/${vendorId}?companyId=${companyId}`);
          const productItemsWithExpenseAccounts = Array.isArray(productRes.data.data) ? productRes.data.data : [];
          
          logger.log(`Product items with expense accounts for vendor ${vendorId}:`, productItemsWithExpenseAccounts);
          
          // Add product expense account mappings
          productItemsWithExpenseAccounts.forEach(productItem => {
            if (productItem.expenseAccountId && productItem.expenseAccount) {
              allAccountMappings.push({
                type: 'product',
                accountId: productItem.expenseAccountId,
                description: `Product Expense - ${productItem.productName}`,
                itemName: productItem.productName,
                accountNumber: productItem.expenseAccount.accountNumber,
                accountType: productItem.expenseAccount.accountType,
                productItemId: productItem.productItemId,
                purchasePrice: productItem.purchasePrice,
                purchaseDescription: productItem.purchaseDescription
              });
            }
          });
        } catch (err) {
          logger.error('Error fetching product items expense accounts:', err);
        }

        // Fetch service items expense accounts from the new API
        try {
          const serviceRes = await ApiService.get(`/vendor/service-items/service-items-expense-accounts/${vendorId}?companyId=${companyId}`);
          const serviceItemsWithExpenseAccounts = Array.isArray(serviceRes.data.data) ? serviceRes.data.data : [];
          
          logger.log(`Service items with expense accounts for vendor ${vendorId}:`, serviceItemsWithExpenseAccounts);
          
          // Add service expense account mappings
          serviceItemsWithExpenseAccounts.forEach(serviceItem => {
            if (serviceItem.expenseAccountId && serviceItem.expenseAccount) {
              allAccountMappings.push({
                type: 'service',
                accountId: serviceItem.expenseAccountId,
                description: `Service Expense - ${serviceItem.serviceName}`,
                itemName: serviceItem.serviceName,
                accountNumber: serviceItem.expenseAccount.accountNumber,
                accountType: serviceItem.expenseAccount.accountType,
                serviceItemId: serviceItem.serviceItemId,
                purchasePrice: serviceItem.purchasePrice,
                purchaseDescription: serviceItem.purchaseDescription
              });
            }
          });
        } catch (err) {
          logger.error('Error fetching service items expense accounts:', err);
        }
        
        logger.log('All account mappings:', allAccountMappings);
        
        // Store account mappings for reference but don't auto-populate rows
        this.expenseAccountMappings = allAccountMappings;
        
        // Expense account mappings are stored but no notification is shown
        // Users can manually add accounts in the Itemize by Account section
      } catch (err) {
        logger.error('Error fetching expense accounts:', err);
        // Don't show error toast as this is supplementary data
      }
    },
    // Calculate total amount for consultant timesheets by expense account ID
    getConsultantTotalByAccountId(accountId) {
      return this.consultantTimesheets.reduce((total, consultant) => {
        // Find the consultant's expense account ID from the mappings
        const consultantMapping = this.expenseAccountMappings.find(
          (m) =>
            m.type === 'consultant' &&
            String(m.accountId) === String(accountId) &&
            String(m.consultantId) === String(consultant.id)
        );
        
        if (consultantMapping) {
          const hours = Number(consultant.hours) || 0;
          const hourlyRate = Number(consultant.hourlyRate) || 0;
          return total + (hours * hourlyRate);
        }
        return total;
      }, 0);
    },
    // Calculate total amount for product/service items by expense account ID
    getProductServiceTotalByAccountId(accountId) {
      return this.productRows.reduce((total, row) => {
        // Find the product/service's expense account ID from the mappings
        const productServiceMapping = this.expenseAccountMappings.find(m => 
          (m.type === 'product' || m.type === 'service') && 
          m.accountId === accountId && 
          ((m.type === 'product' && m.productItemId === row.itemId) || 
           (m.type === 'service' && m.serviceItemId === row.itemId))
        );
        
        if (productServiceMapping && row.quantity && row.unitPrice) {
          return total + ((Number(row.quantity) || 0) * (Number(row.unitPrice) || 0));
        }
        return total;
      }, 0);
    },
    // Update account row amounts based on calculated totals
    updateAccountRowAmounts() {
      this.accountRows.forEach(row => {
        if (row.accountId) {
          const consultantTotal = this.getConsultantTotalByAccountId(row.accountId);
          const productServiceTotal = this.getProductServiceTotalByAccountId(row.accountId);
          row.amount = consultantTotal + productServiceTotal;
        }
      });
    },
    // Get detailed breakdown for a specific account - showing individual records from Product/Service section
    getAccountBreakdown(accountId) {
      const breakdown = [];
      
      // Add consultant timesheet breakdowns (these are separate from product/service items)
      this.consultantTimesheets.forEach(consultant => {
        const consultantMapping = this.expenseAccountMappings.find(
          (m) =>
            m.type === 'consultant' &&
            String(m.accountId) === String(accountId) &&
            String(m.consultantId) === String(consultant.id)
        );
        
        if (consultantMapping && consultant.timesheetId) {
          const hours = Number(consultant.hours) || 0;
          const hourlyRate = Number(consultant.hourlyRate) || 0;
          const amount = hours * hourlyRate;
          
          if (amount > 0) {
            breakdown.push({
              type: 'consultant',
              id: consultant.timesheetId,
              name: consultant.name,
              description: consultant.description,
              hours: hours,
              hourlyRate: hourlyRate,
              amount: amount,
              source: 'Consultant Timesheet'
            });
          }
        }
      });
      
      // Add individual product/service item records that correspond to this account
      this.productRows.forEach((row, rowIndex) => {
        if (row.itemId && row.itemType && row.quantity && row.unitPrice) {
          const productServiceMapping = this.expenseAccountMappings.find(m => 
            (m.type === 'product' || m.type === 'service') && 
            m.accountId === accountId && 
            ((m.type === 'product' && m.productItemId === row.itemId) || 
             (m.type === 'service' && m.serviceItemId === row.itemId))
          );
          
          if (productServiceMapping) {
            const quantity = Number(row.quantity) || 0;
            const unitPrice = Number(row.unitPrice) || 0;
            const amount = quantity * unitPrice;
            
            if (amount > 0) {
              // Get the source item details
              const sourceItem = this.productServiceList.find(i => i.id === row.itemId && i.type === row.itemType);
              
              breakdown.push({
                type: row.itemType,
                id: row.itemId,
                name: row.description || sourceItem?.name || 'Unnamed Item',
                description: `${row.itemType} Item`,
                quantity: quantity,
                unitPrice: unitPrice,
                amount: amount,
                source: `Product/Service Row ${rowIndex + 1}`,
                rowIndex: rowIndex,
                sourceItem: sourceItem
              });
            }
          }
        }
      });
      
      return breakdown;
    },
    // Get source information for a specific item
    getItemSourceInfo(itemId, itemType) {
      if (!itemId || !itemType) return '-';
      
      // Find the item in productServiceList
      const item = this.productServiceList.find(i => i.id === itemId && i.type === itemType);
      if (item) {
        return `${itemType} from vendor catalog`;
      }
      
      return 'Unknown source';
    },
    // Get detailed source records for a specific item
    getItemSourceRecords(itemId, itemType) {
      if (!itemId || !itemType) return [];
      
      const sourceRecords = [];
      
      // Find the item in productServiceList
      const item = this.productServiceList.find(i => i.id === itemId && i.type === itemType);
      if (item) {
        sourceRecords.push({
          id: item.id,
          type: `vendor-${itemType}`,
          name: item.name,
          description: item.description || item.name,
          quantity: 1, // Default quantity from catalog
          unitPrice: item.purchasePrice || item.salesPrice || 0,
          total: item.purchasePrice || item.salesPrice || 0,
          billStatus: 'Available', // This would come from the backend
          vendorId: item.preferredVendor
        });
      }
      
      return sourceRecords;
    },
    // Get CSS class for bill status
    getBillStatusClass(billStatus) {
      switch (billStatus) {
        case 'Available':
          return 'vendor_create_bill-status-available';
        case 'Bill Created':
          return 'vendor_create_bill-status-bill-created';
        case 'Paid':
          return 'vendor_create_bill-status-paid';
        case 'Not Applicable':
          return 'vendor_create_bill-status-not-applicable';
        default:
          return 'vendor_create_bill-status-default';
      }
    },
    autoPopulateAccountRows(allAccountMappings) {
      logger.log('autoPopulateAccountRows called with:', allAccountMappings);
      // Store the mappings for amount calculations
      this.expenseAccountMappings = allAccountMappings;
      
      // Clear existing account rows
      this.accountRows = [];
      
      allAccountMappings.forEach(mapping => {
        // Add expense account if available
        if (mapping.accountId) {
          this.accountRows.push({
            accountId: mapping.accountId,
            description: mapping.description,
            amount: 0
          });
        }
      });
      
      // Only show feedback and add default row if there are actual mappings
      if (allAccountMappings.length > 0) {
        // Update amounts based on current data
        this.updateAccountRowAmounts();
        
        // Expense accounts are auto-populated silently
        // No notification is shown to the user
      } else {
        // No mappings found - clear account rows and show appropriate message
        this.accountRows = [];
        logger.log('No expense account mappings found for this vendor');
      }
    },
    addConsultantTimesheet() {
      // Get currently available options (not yet added to bill)
      const availableOptions = this.getAvailableConsultantOptions();
      
      if (availableOptions.length > 0) {
        // Add the first available timesheet record
        const availableOption = availableOptions[0];
        this.consultantTimesheets.push({
          id: availableOption.id,
          name: availableOption.name,
          description: availableOption.description,
          hours: Number(availableOption.hours) || 0,
          hourlyRate: Number(availableOption.hourlyRate) || 0,
          timesheetId: availableOption.timesheetId,
          attachment: availableOption.attachment || null
        });
        
        // Show success message
        this.showSuccessModal('Success', `Added consultant timesheet: ${availableOption.name} - ${availableOption.description}`);
      } else {
        // If no available timesheets, add empty row
        this.consultantTimesheets.push({ id: '', name: '', description: '', hours: 0, hourlyRate: 0 });
      }
    },
    getAvailableConsultantOptions() {
      // Get all timesheet IDs currently added to bill
      const addedTimesheetIds = this.consultantTimesheets
        .filter(c => c.timesheetId)
        .map(c => c.timesheetId);
      
      // Return options that are NOT currently added to the bill
      return this.consultantOptions.filter(option => 
        !addedTimesheetIds.includes(option.timesheetId)
      );
    },
    removeConsultantTimesheet(idx) {
      this.consultantTimesheets.splice(idx, 1);
      // Timesheet removed silently - no notification shown
    },
    onConsultantChange(idx) {
      const selectedName = this.consultantTimesheets[idx].name;
      const found = this.consultantOptions.find(opt => opt.name === selectedName);
      if (found) {
        this.consultantTimesheets[idx].description = found.description;
        this.consultantTimesheets[idx].hourlyRate = Number(found.hourlyRate) || 0;
        this.consultantTimesheets[idx].id = found.id;
        // Pre-fill hours with timesheet hours if available
        if (found.hours && Number(found.hours) > 0) {
          this.consultantTimesheets[idx].hours = Number(found.hours);
        }
        // Store timesheet ID if available
        if (found.timesheetId) {
          this.consultantTimesheets[idx].timesheetId = found.timesheetId;
        }
        if (found.attachment) {
          this.consultantTimesheets[idx].attachment = found.attachment;
        }
      } else {
        this.consultantTimesheets[idx].id = '';
        this.consultantTimesheets[idx].timesheetId = null;
        this.consultantTimesheets[idx].attachment = null;
      }
    },
    getConsultantTotal(consultant) {
      const hours = Number(consultant.hours) || 0;
      const hourlyRate = Number(consultant.hourlyRate) || 0;
      return (hours * hourlyRate).toFixed(2);
    },
    getConsultantHoursTotal(consultant) {
      const hours = Number(consultant.hours) || 0;
      const hourlyRate = Number(consultant.hourlyRate) || 0;
      return (hours * hourlyRate).toFixed(2);
    },
    createEmptyConsultantHoursRow() {
      return {
        id: '',
        description: '',
        hours: 0,
        hourlyRate: 0,
        timesheetId: null,
        timesheetData: null,
        availableTimesheets: [],
        selectedTimesheetId: ''
      };
    },
    addConsultantHoursRow() {
      this.consultantHoursRows.push(this.createEmptyConsultantHoursRow());
    },
    removeConsultantHoursRow(idx) {
      this.consultantHoursRows.splice(idx, 1);
    },
    async handleConsultantHoursSelect(index, consultantId) {
      if (consultantId === '__create_consultant__') {
        // reset selection and open creation dialog
        this.$nextTick(() => {
          if (this.consultantHoursRows[index]) {
            this.consultantHoursRows[index].id = '';
          }
        });
        this.currentConsultantHoursIndex = index;
        this.showConsultantCreationDialog = true;
        return;
      }
      if (!consultantId) {
        this.consultantHoursRows[index] = { id: '', description: '', hours: 0, hourlyRate: 0, timesheetId: null, timesheetData: null, availableTimesheets: [], selectedTimesheetId: '' };
        return;
      }
      
      const selectedConsultant = this.allCompanyConsultants.find(c => 
        String(c.id) === String(consultantId)
      );
      
      if (selectedConsultant) {
        // IMPORTANT: For multi-customer consultants, do NOT use consultant model fields
        // The hourlyRate will be set from ConsultantCustomerService when a timesheet is selected
        let hourlyRate = 0;
        if (selectedConsultant.can_work_multiple_customers === true) {
          // For multi-customer consultants, set hourlyRate to 0 initially
          // It will be updated from ConsultantCustomerService.purchasePrice when timesheet is selected
          hourlyRate = 0;
          logger.log('Multi-customer consultant selected - hourlyRate will be set from ConsultantCustomerService when timesheet is selected');
        } else {
          // For single-customer consultants, use consultant model fields
          hourlyRate = Number(selectedConsultant.purchasePrice) || 0;
        }
        
        Object.assign(this.consultantHoursRows[index], {
          id: String(consultantId),
          hourlyRate: hourlyRate,
          availableTimesheets: [],
          selectedTimesheetId: '',
          timesheetId: null,
          timesheetData: null,
          hours: 0,
          can_work_multiple_customers: selectedConsultant.can_work_multiple_customers || false // Store for later use
        });
        
        // Auto-select vendor based on consultant's preferredVendorId ONLY when vendor is not selected yet.
        // If a vendor is already selected, we must not override it (multi-agreement consultants can have a
        // "primary" preferredVendorId that is unrelated to the bill we are creating).
        if (!this.vendorId && selectedConsultant.preferredVendorId) {
          const preferredVendorIdStr = String(selectedConsultant.preferredVendorId);
          const preferredVendor = this.vendorList.find(v => String(v.id) === preferredVendorIdStr);
          
          if (preferredVendor) {
            this.vendorId = preferredVendorIdStr;
            logger.log(`Auto-selected vendor ${preferredVendor.vendorName} based on consultant's preferredVendorId`);
          } else {
            logger.warn(`Consultant has preferredVendorId ${selectedConsultant.preferredVendorId} but vendor not found in vendorList`);
          }
        }
        
        // Fetch available timesheets for this consultant
        if (consultantId) {
          logger.log('About to fetch timesheets for consultantId:', consultantId, 'at index:', index);
          await this.fetchConsultantHoursTimesheets(index, consultantId);
          if (this.vendorId) {
            await this.fetchExpenseAccounts(this.vendorId);
            await this.$nextTick();
          }
        }
      }
    },
    async ensureConsultantInCompanyList(consultantId) {
      const found = (this.allCompanyConsultants || []).some(
        (c) => String(c.id) === String(consultantId)
      );
      if (found) return;
      try {
        const consultant = await ApiService.getConsultantById(consultantId);
        if (consultant && consultant.id != null) {
          this.allCompanyConsultants = [...(this.allCompanyConsultants || []), consultant];
        }
      } catch (e) {
        logger.warn('Could not fetch consultant for route prefill:', e);
      }
    },
    async loadDraftLinkedTimesheetIds({ force = false } = {}) {
      const companyId = this.$route.params.id;
      const vendorId = this.vendorId;
      if (!companyId || !vendorId) {
        this.draftLinkedTimesheetIds = [];
        this.draftLinkedTimesheetIdsVendorId = null;
        this.draftLinkedTimesheetIdsExcludeBillId = null;
        return [];
      }

      const excludeBillId = this.editingBillId ? String(this.editingBillId) : '';
      if (
        !force &&
        this.draftLinkedTimesheetIdsVendorId != null &&
        String(this.draftLinkedTimesheetIdsVendorId) === String(vendorId) &&
        String(this.draftLinkedTimesheetIdsExcludeBillId || '') === excludeBillId &&
        Array.isArray(this.draftLinkedTimesheetIds)
      ) {
        return this.draftLinkedTimesheetIds;
      }

      try {
        const params = new URLSearchParams({ vendorId: String(vendorId) });
        if (excludeBillId) {
          params.set('excludeBillId', excludeBillId);
        }
        const res = await ApiService.get(
          `/vendor/enter-bill/company/${companyId}/draft-timesheet-ids?${params.toString()}`
        );
        const ids = Array.isArray(res.data?.timesheetIds)
          ? res.data.timesheetIds.map((id) => String(id))
          : [];
        this.draftLinkedTimesheetIds = ids;
        this.draftLinkedTimesheetIdsVendorId = vendorId;
        this.draftLinkedTimesheetIdsExcludeBillId = excludeBillId;
        return ids;
      } catch (error) {
        logger.warn('Could not load draft-linked timesheet IDs:', error);
        this.draftLinkedTimesheetIds = [];
        this.draftLinkedTimesheetIdsVendorId = vendorId;
        this.draftLinkedTimesheetIdsExcludeBillId = excludeBillId;
        return [];
      }
    },
    async getBillableTimesheetsForConsultant(consultantId, { specificTimesheetId = null } = {}) {
      const companyId = this.$route.params.id;
      const consultant = this.allCompanyConsultants.find(
        (c) => String(c.id) === String(consultantId)
      );
      const canWorkMultipleCustomers = consultant?.can_work_multiple_customers === true;

      let customerServiceIds = [];
      if (canWorkMultipleCustomers && this.vendorId) {
        try {
          const serviceUrl = companyId
            ? `/customer/consultant-customer-services?preferredVendorId=${this.vendorId}&companyId=${companyId}&consultantId=${consultantId}`
            : `/customer/consultant-customer-services?preferredVendorId=${this.vendorId}&consultantId=${consultantId}`;
          const serviceResponse = await ApiService.get(serviceUrl);
          const services = Array.isArray(serviceResponse.data) ? serviceResponse.data : [];
          customerServiceIds = services.map((s) => String(s.id));
        } catch (error) {
          logger.error('Error fetching ConsultantCustomerService records:', error);
        }
      }

      const url = companyId
        ? `/consultant/timesheet?companyId=${companyId}&consultantId=${consultantId}`
        : `/consultant/timesheet?consultantId=${consultantId}`;
      const response = await ApiService.get(url);
      const allTimesheets = response.data?.data || response.data || [];

      // Timesheets already on a Draft bill (invoice auto-draft or portal) stay on Draft Bills
      const draftLinkedIds = await this.loadDraftLinkedTimesheetIds();
      const draftLinkedSet = new Set((draftLinkedIds || []).map((id) => String(id)));

      const consultantTimesheets = allTimesheets.filter((ts) => {
        const consultantMatches = String(ts.consultantId) === String(consultantId);
        const billStatusMatches = !ts.billStatus || ts.billStatus === 'Not Applicable';
        // Vendor-portal submissions are billed from Draft Bills → Submitted, not here
        const isVendorPortalTimesheet =
          ts.isVendorInvoice === true || ts.isVendorInvoice === 'true';
        const alreadyOnDraftBill = draftLinkedSet.has(String(ts.id));
        const isSpecificTimesheet = specificTimesheetId && String(ts.id) === String(specificTimesheetId);

        // Allow a specific timesheet when loading an existing draft (incl. Submitted → Create Bill)
        if (isSpecificTimesheet) {
          return consultantMatches;
        }

        if (!consultantMatches || !billStatusMatches || isVendorPortalTimesheet || alreadyOnDraftBill) {
          return false;
        }

        if (canWorkMultipleCustomers) {
          if (customerServiceIds.length > 0) {
            const timesheetServiceId = ts.consultantCustomerServiceId;
            return timesheetServiceId && customerServiceIds.includes(String(timesheetServiceId));
          }
          if (ts.consultantCustomerService) {
            const servicePreferredVendorId = ts.consultantCustomerService.preferredVendorId;
            return servicePreferredVendorId && String(servicePreferredVendorId) === String(this.vendorId);
          }
          return false;
        }

        return true;
      });

      consultantTimesheets.forEach((ts) => {
        if (ts.attachments && ts.attachments.length > 0) {
          ts.attachments = ts.attachments.map((att) => ({
            ...att,
            source: 'timesheet'
          }));
        }
      });

      return consultantTimesheets;
    },
    async applyRouteConsultantPrefill() {
      if (!this.isConsultantIndustry) {
        return;
      }
      if (this.loadingExistingBill || this.editingBillId || this.$route.query.fromCaptured) {
        return;
      }
      const q = this.$route.query;
      const consultantId = q.consultantId ? String(q.consultantId) : '';
      const filterByConsultant = q.filterByConsultant === 'true';
      const specificTimesheetId = q.timesheetId ? String(q.timesheetId) : '';
      if (filterByConsultant) {
        this.activeTab = 'consultant';
      }

      if (!consultantId || !this.vendorId) {
        return;
      }

      const prefilledRows = this.consultantHoursRows.filter(
        (r) => String(r.id) === consultantId && r.timesheetId
      );
      if (this.routeConsultantPrefillApplied && prefilledRows.length > 0) {
        if (specificTimesheetId) {
          if (prefilledRows.some((r) => String(r.timesheetId) === specificTimesheetId)) {
            return;
          }
        } else if (filterByConsultant) {
          return;
        }
      }

      await this.ensureConsultantInCompanyList(consultantId);

      let billableTimesheets = [];
      try {
        billableTimesheets = await this.getBillableTimesheetsForConsultant(consultantId, {
          specificTimesheetId: specificTimesheetId || null
        });
      } catch (error) {
        logger.error('Error loading billable timesheets for consultant prefill:', error);
      }

      if (q.consultantCustomerServiceId) {
        billableTimesheets = billableTimesheets.filter(
          (ts) =>
            ts.consultantCustomerServiceId &&
            String(ts.consultantCustomerServiceId) === String(q.consultantCustomerServiceId)
        );
      }

      let timesheetsToAdd = billableTimesheets;
      if (specificTimesheetId) {
        timesheetsToAdd = billableTimesheets.filter(
          (ts) => String(ts.id) === specificTimesheetId
        );
      }

      const addAllFromConsultantCenter = filterByConsultant && !specificTimesheetId;

      if (addAllFromConsultantCenter && timesheetsToAdd.length > 0) {
        this.consultantHoursRows = [];
        for (let i = 0; i < timesheetsToAdd.length; i++) {
          this.consultantHoursRows.push(this.createEmptyConsultantHoursRow());
          await this.handleConsultantHoursSelect(i, consultantId);
          await this.handleConsultantHoursTimesheetSelect(i, String(timesheetsToAdd[i].id));
        }
      } else {
        if (!this.consultantHoursRows.length) {
          this.consultantHoursRows = [this.createEmptyConsultantHoursRow()];
        }
        const index = 0;
        await this.handleConsultantHoursSelect(index, consultantId);

        if (
          this.consultantHoursRows[index] &&
          String(this.consultantHoursRows[index].id) !== consultantId
        ) {
          this.consultantHoursRows[index].id = consultantId;
          await this.fetchConsultantHoursTimesheets(index, consultantId);
        }

        let timesheetIdToSelect = specificTimesheetId;
        if (!timesheetIdToSelect) {
          const available = this.consultantHoursRows[index]?.availableTimesheets || [];
          if (available.length > 0) {
            timesheetIdToSelect = String(available[0].id);
          }
        }
        if (timesheetIdToSelect) {
          await this.handleConsultantHoursTimesheetSelect(index, timesheetIdToSelect);
        }
      }

      if (this.vendorId) {
        await this.fetchExpenseAccounts(this.vendorId);
      }

      this.routeConsultantPrefillApplied = true;
    },
    async fetchConsultantHoursTimesheets(index, consultantId) {
      try {
        const consultantTimesheets = await this.getBillableTimesheetsForConsultant(consultantId);
        if (this.consultantHoursRows[index]) {
          this.consultantHoursRows[index].availableTimesheets = consultantTimesheets;
        }
        this.refreshAvailableTimesheetsForConsultantHoursRows();
      } catch (error) {
        logger.error('Error fetching timesheets:', error);
        if (this.consultantHoursRows[index]) {
          this.consultantHoursRows[index].availableTimesheets = [];
        }
      }
    },
    formatTimesheetDateUs(value) {
      if (!value) return '';
      const raw = String(value).trim();
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) return raw;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return this.formatDateForDatePicker(d);
    },
    buildTimesheetDescription(timesheet) {
      if (!timesheet) return '';
      const explicit = String(timesheet.description || '').trim();
      if (explicit) return explicit;
      const from = this.formatTimesheetDateUs(timesheet.fromDate || timesheet.from_date);
      const to = this.formatTimesheetDateUs(timesheet.toDate || timesheet.to_date);
      if (from && to) return `${from} to ${to}`;
      if (from) return from;
      if (to) return to;
      return '';
    },
    async handleConsultantHoursTimesheetSelect(index, timesheetId) {
      if (!timesheetId) {
        // Clear timesheet data if empty selection
        this.consultantHoursRows[index].timesheetId = null;
        this.consultantHoursRows[index].timesheetData = null;
        this.consultantHoursRows[index].hours = 0;
        this.consultantHoursRows[index].description = '';
        this.refreshAvailableTimesheetsForConsultantHoursRows();
        return;
      }
      
      // Handle "Create New Timesheet" option
      if (timesheetId === '__create_timesheet__') {
        // Reset the selection to empty
        this.consultantHoursRows[index].selectedTimesheetId = '';
        // Open the timesheet modal
        this.openConsultantHoursTimesheetModal(index);
        return;
      }
      
      try {
        // Find the selected timesheet from available timesheets
        const selectedTimesheet = this.consultantHoursRows[index].availableTimesheets.find(
          ts => String(ts.id) === String(timesheetId)
        );
        
        if (selectedTimesheet) {
          // Keep dropdown selection in sync
          this.consultantHoursRows[index].selectedTimesheetId = String(selectedTimesheet.id);
          // Populate the consultant row with timesheet data
          this.consultantHoursRows[index].timesheetId = selectedTimesheet.id;
          
          // Add source field to attachments if they exist
          if (selectedTimesheet.attachments && selectedTimesheet.attachments.length > 0) {
            selectedTimesheet.attachments = selectedTimesheet.attachments.map(att => ({
              ...att,
              source: 'timesheet'
            }));
          }
          
          this.consultantHoursRows[index].timesheetData = selectedTimesheet;
          this.consultantHoursRows[index].hours = Number(selectedTimesheet.hours) || 0;
          this.consultantHoursRows[index].description = this.buildTimesheetDescription(selectedTimesheet);
          
          // Update hourlyRate: use ConsultantCustomerService.purchasePrice if can_work_multiple_customers = true
          const consultant = this.consultantHoursRows[index];
          if (consultant.can_work_multiple_customers === true && selectedTimesheet.consultantCustomerService && selectedTimesheet.consultantCustomerService.purchasePrice) {
            const servicePurchasePrice = Number(selectedTimesheet.consultantCustomerService.purchasePrice) || 0;
            this.consultantHoursRows[index].hourlyRate = servicePurchasePrice;
            logger.log('Updated hourlyRate from ConsultantCustomerService.purchasePrice:', {
              consultantId: consultant.id,
              consultantCustomerServiceId: selectedTimesheet.consultantCustomerService.id,
              purchasePrice: servicePurchasePrice
            });
          }
          
          logger.log('Selected timesheet data:', selectedTimesheet);

          // Refresh vendor→consultant expense mappings so **Itemize by Account** auto-rows resolve (id type can differ)
          if (this.vendorId) {
            await this.fetchExpenseAccounts(this.vendorId);
            await this.$nextTick();
          }
          
          // Refresh available timesheets for all other rows to exclude the newly selected timesheet
          this.refreshAvailableTimesheetsForConsultantHoursRows();
        }
      } catch (error) {
        logger.error('Error selecting timesheet:', error);
      }
    },
    refreshAvailableTimesheetsForConsultantHoursRows() {
      const rows = Array.isArray(this.consultantHoursRows) ? this.consultantHoursRows : [];
      // Collect selected timesheet ids across all rows
      const selectedIds = rows
        .filter(r => r && r.timesheetId)
        .map(r => String(r.timesheetId));
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row || !row.id || !Array.isArray(row.availableTimesheets)) continue;
        
        // Exclude ids selected in other rows, but keep the currently-selected one visible in its own row
        const excluded = selectedIds.filter(id => id !== String(row.timesheetId || ''));
        row.availableTimesheets = row.availableTimesheets.filter(ts => !excluded.includes(String(ts.id)));
      }
    },
    formatTimesheetOption(timesheet) {
      // Format: Date Range - Hours (US MM/DD/YYYY)
      const fromDate = this.formatTimesheetDateUs(timesheet.fromDate || timesheet.from_date);
      const toDate = this.formatTimesheetDateUs(timesheet.toDate || timesheet.to_date);
      const hours = timesheet.hours || 0;
      
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate} (${hours} hrs)`;
      } else if (fromDate) {
        return `${fromDate} (${hours} hrs)`;
      } else {
        return `Timesheet #${timesheet.id} (${hours} hrs)`;
      }
    },
    getConsultantHoursTimesheetUrl(timesheetId) {
      return `/timesheet/${timesheetId}`;
    },
    openConsultantHoursTimesheetModal(index) {
      if (index === undefined || index === null) {
        logger.log('No consultant index provided');
        return;
      }
      
      const consultant = this.consultantHoursRows[index];
      if (!consultant.id) {
        logger.log('No consultant selected');
        return;
      }
      
      // Store the index for later use
      this.currentTimesheetIndex = index;
      
      // Find full consultant data (tolerant matching like CustomerCreateInvoice.vue)
      const fullConsultantData =
        (Array.isArray(this.allCompanyConsultants) ? this.allCompanyConsultants : []).find(
          c => String(c?.id) === String(consultant.id)
        );
      if (fullConsultantData) {
        this.prefilledConsultantData = {
          ...fullConsultantData,
          consultantName: fullConsultantData.consultantName || fullConsultantData.name,
          // Important for multi-agreement consultants: scope agreement list to the selected vendor
          preferredVendorId: this.vendorId || null
        };
        logger.log('Opening timesheet modal with consultant data:', this.prefilledConsultantData);
        this.showTimesheetModal = true;
      } else {
        // Fallback with current row values so popup still opens.
        this.prefilledConsultantData = {
          id: consultant.id,
          consultantId: consultant.id,
          consultantName: consultant.consultantName || consultant.name || '',
          name: consultant.consultantName || consultant.name || '',
          preferredVendorId: this.vendorId || null
        };
        logger.log('Consultant not found in list, opening modal with row data:', this.prefilledConsultantData);
        this.showTimesheetModal = true;
      }
    },
    async handleTimesheetSuccess(timesheetData) {
      logger.log('Timesheet created successfully:', timesheetData);
      
      if (this.currentTimesheetIndex !== null && timesheetData) {
        const index = this.currentTimesheetIndex;
        const consultant = this.consultantHoursRows[index];
        
        if (!consultant) {
          logger.error('Consultant row not found at index:', index);
          this.closeTimesheetModal();
          return;
        }
        
        // Ensure timesheet data has all required fields for dropdown display
        const formattedTimesheet = {
          ...timesheetData,
          id: timesheetData.id,
          consultantId: consultant.id,
          hours: timesheetData.hours || 0,
          fromDate: timesheetData.fromDate || timesheetData.from_date || '',
          toDate: timesheetData.toDate || timesheetData.to_date || '',
          status: timesheetData.status || 'Time Sheet Created',
          billStatus: timesheetData.billStatus || timesheetData.bill_status || 'Not Applicable',
          // Add source field to attachments if they exist
          attachments: timesheetData.attachments && timesheetData.attachments.length > 0
            ? timesheetData.attachments.map(att => ({
                ...att,
                source: 'timesheet'
              }))
            : []
        };
        
        // Ensure availableTimesheets array exists
        if (!consultant.availableTimesheets || !Array.isArray(consultant.availableTimesheets)) {
          consultant.availableTimesheets = [];
        }
        
        // Check if timesheet already exists in availableTimesheets
        const existingIndex = consultant.availableTimesheets.findIndex(
          ts => String(ts.id) === String(timesheetData.id)
        );
        
        if (existingIndex >= 0) {
          // Update existing timesheet entry - create new array reference for Vue 3 reactivity
          consultant.availableTimesheets = consultant.availableTimesheets.map((ts, idx) => 
            idx === existingIndex ? formattedTimesheet : ts
          );
        } else {
          // Add new timesheet to available timesheets - create new array reference for Vue reactivity
          consultant.availableTimesheets = [...consultant.availableTimesheets, formattedTimesheet];
        }
        
        // Wait for Vue to update the DOM with the new timesheet option
        await this.$nextTick();
        
        // Update consultant row with timesheet data (Vue 3 handles reactivity automatically)
        consultant.timesheetId = formattedTimesheet.id;
        consultant.timesheetData = formattedTimesheet;
        consultant.hours = formattedTimesheet.hours || consultant.hours || 0;
        consultant.description = this.buildTimesheetDescription(formattedTimesheet);
        
        // Ensure hourlyRate is set correctly after creating a timesheet
        try {
          if (consultant.can_work_multiple_customers === true) {
            const serviceId = formattedTimesheet.consultantCustomerServiceId || formattedTimesheet.consultant_customer_service_id;
            if (serviceId) {
              const serviceRes = await ApiService.get(`/customer/consultant-customer-services/${serviceId}`);
              const service = serviceRes?.data || null;
              if (service) {
                consultant.timesheetData.consultantCustomerService = service;
                if (service.purchasePrice != null) {
                  consultant.hourlyRate = Number(service.purchasePrice) || 0;
                }
              }
            }
          } else {
            // Single-agreement: fall back to consultant's purchasePrice if hourlyRate is empty/0
            const fullConsultant = (Array.isArray(this.allCompanyConsultants) ? this.allCompanyConsultants : []).find(
              c => String(c?.id) === String(consultant.id)
            );
            if ((Number(consultant.hourlyRate) || 0) <= 0 && fullConsultant) {
              consultant.hourlyRate = Number(fullConsultant.purchasePrice) || 0;
            }
          }
        } catch (e) {
          logger.error('Failed to set hourlyRate after timesheet creation:', e);
        }
        
        // Set selectedTimesheetId - ensure it matches the option value type (string)
        // HTML select options convert values to strings, so we use String() for consistency
        const timesheetIdString = String(formattedTimesheet.id);
        consultant.selectedTimesheetId = timesheetIdString;
        
        // Wait again to ensure dropdown updates
        await this.$nextTick();
        
        logger.log('Updated consultant row:', consultant);
        logger.log('Available timesheets after update:', consultant.availableTimesheets);
        logger.log('Selected timesheet ID:', consultant.selectedTimesheetId);
        
        // Refresh expense accounts after creating timesheet to ensure account mappings are updated
        // This ensures the visibleAccountRows computed property can find the mapping
        if (this.vendorId && consultant.id) {
          await this.fetchExpenseAccounts(this.vendorId);
          await this.$nextTick();
        }
        
        // Force Vue to update the dropdown by triggering a re-render
        await this.$nextTick();
        
        // Exclude selected timesheets from other rows (prevent duplicates)
        this.refreshAvailableTimesheetsForConsultantHoursRows();
      }
      
      this.closeTimesheetModal();
    },
    closeTimesheetModal() {
      this.showTimesheetModal = false;
      this.prefilledConsultantData = null;
      this.currentTimesheetIndex = null;
    },
    async viewConsultantHoursAttachment(attachment, consultantId) {
      if (!attachment || !attachment.filePath) return;
      
      try {
        // Check if the filePath is a base64 data URL (for newly uploaded files)
        if (attachment.filePath.startsWith('data:')) {
          // For base64 attachments, open directly
          window.open(attachment.filePath, '_blank');
          return;
        }
        // Get companyId for nested directory structure
        const companyId = this.$route.params.id;
        
        // Handle different attachment path formats
        let filename = attachment.filePath;
        
        // If filePath is a full URL, extract the filename
        if (attachment.filePath.includes('/')) {
          filename = attachment.filePath.split('/').pop();
        }
        
        // Determine if this is a timesheet attachment
        let viewUrl;
        if (attachment.source && attachment.source === 'timesheet') {
          // Timesheet attachment reference - use invoice attachment endpoint with source param and consultantId
          const queryParams = consultantId 
            ? `companyId=${companyId}&source=timesheet&consultantId=${consultantId}`
            : `source=timesheet`;
          viewUrl = companyId 
            ? `/customer/invoice-attachments/${filename}?${queryParams}`
            : `/customer/invoice-attachments/${filename}?${queryParams}`;
        } else {
          // Default: try consultant attachment endpoint (for backwards compatibility)
          viewUrl = companyId 
            ? `/consultant/attachment/${filename}?companyId=${companyId}`
            : `/consultant/attachment/${filename}`;
        }
        
        // Fetch the file with authentication
        const response = await ApiService.get(viewUrl, {
          responseType: 'blob'
        });
        
        // Determine MIME type from response or use attachment mimeType
        const mimeType = response.headers['content-type'] || attachment.mimeType || 'application/pdf';
        
        // Create a blob URL and open in new window
        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Clean up the URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      } catch (error) {
        logger.error('Failed to view attachment:', error);
        this.errorMessage = 'Failed to view attachment';
        this.showErrorModal = true;
      }
    },
    /** Full vendor-scoped consultant list for each row — same consultant may appear on multiple rows (different timesheets). */
    getAvailableConsultantsForHours() {
      if (!this.allCompanyConsultants || this.allCompanyConsultants.length === 0) {
        logger.log('⚠️ No consultants available - allCompanyConsultants is empty');
        return [];
      }
      
      const availableConsultants = this.allCompanyConsultants.filter(consultant => {
        if (consultant.consultantType || consultant.consultant_type || consultant.type) {
          const type = consultant.consultantType || consultant.consultant_type || consultant.type;
          return type === 'C2C' || type === 'c2c';
        }
        return true;
      });
      
      logger.log('📊 Available consultants for hours:', availableConsultants);
      return availableConsultants;
    },
    getAttachmentUrl(filename) {
      if (!filename) return '#';
      // Get companyId for nested directory structure
      const companyId = this.$route.params.id || this.$route.params.companyId;
      if (companyId) {
        return `${process.env.VUE_APP_API_URL}/consultant/attachment/${filename}?companyId=${companyId}`;
      }
      return `${process.env.VUE_APP_API_URL}/consultant/attachment/${filename}`;
    },
    // Bill-level attachments (reference: CustomerAddNewInvoice.vue)
    handleBillFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      if (!this.compressingAttachments) this.attachmentCompressNote = '';
      const getMimeType = (filename, fallbackType) => {
        if (fallbackType && fallbackType !== '') return fallbackType;
        const extension = filename.split('.').pop().toLowerCase();
        const mimeMap = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'txt': 'text/plain'
        };
        return mimeMap[extension] || 'application/octet-stream';
      };
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const attachment = {
            fileName: file.name,
            fileSize: file.size,
            filePath: e.target.result,
            mimeType: getMimeType(file.name, file.type),
            uploadedAt: new Date().toISOString(),
            source: 'bill',
            file
          };
          if (!Array.isArray(this.attachments)) this.attachments = [];
          const attachmentIndex = this.attachments.length;
          this.attachments.push(attachment);
          this.$nextTick(() => {
            this.attachmentPreviewUrls[attachmentIndex] = URL.createObjectURL(file);
          });
        };
        reader.readAsDataURL(file);
      });
      if (this.$refs.addBillFileInput) {
        this.$refs.addBillFileInput.value = '';
      }
    },
    canCompressAttachment(attachment) {
      return isCompressibleAttachment(attachment);
    },
    attachmentFileKind(attachment) {
      const mime = String(attachment?.mimeType || '').toLowerCase();
      const ext = String(attachment?.fileName || '').split('.').pop().toLowerCase();
      if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
      if (mime.startsWith('image/')) return 'image';
      if (['doc', 'docx'].includes(ext)) return 'doc';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'sheet';
      return 'file';
    },
    attachmentFileIcon(attachment) {
      const icons = {
        pdf: 'bi bi-file-earmark-pdf',
        image: 'bi bi-file-earmark-image',
        doc: 'bi bi-file-earmark-word',
        sheet: 'bi bi-file-earmark-excel',
        file: 'bi bi-file-earmark',
      };
      return icons[this.attachmentFileKind(attachment)];
    },
    compressResultLabel(attachment) {
      switch (attachment.compressionResult) {
        case 'done': return `Compressed from ${this.formatFileSize(attachment.originalFileSize)}`;
        case 'skipped': return 'Already small';
        default: return 'Could not compress';
      }
    },
    async compressBillAttachment(index) {
      const original = this.attachments[index];
      if (this.compressingAttachmentIndex !== null || !isCompressibleAttachment(original)) return null;
      this.compressingAttachmentIndex = index;
      if (!this.attachmentCompressQueue.length) this.attachmentCompressNote = '';
      try {
        const updated = await compressSingleAttachment(original);
        const currentIndex = this.attachments.indexOf(original);
        if (currentIndex === -1) return null;
        this.attachments.splice(currentIndex, 1, updated);
        if (updated.file && updated.file !== original.file) {
          if (this.attachmentPreviewUrls[currentIndex]) URL.revokeObjectURL(this.attachmentPreviewUrls[currentIndex]);
          this.attachmentPreviewUrls[currentIndex] = URL.createObjectURL(updated.file);
        }
        return updated;
      } finally {
        this.compressingAttachmentIndex = null;
      }
    },
    async compressBillAttachmentsForReview() {
      if (this.compressingAttachments) return;
      const targets = (this.attachments || []).filter(isCompressibleAttachment);
      if (!targets.length) return;
      this.attachmentCompressNote = '';
      this.$refs.billAttachmentsSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const beforeBytes = getAttachmentsTotalBytes(this.attachments);
      let compressedCount = 0;
      let failedCount = 0;
      this.attachmentCompressQueue = targets.slice();
      try {
        for (const target of targets) {
          this.attachmentCompressQueue = this.attachmentCompressQueue.filter((att) => att !== target);
          const index = this.attachments.indexOf(target);
          if (index === -1) continue;
          const updated = await this.compressBillAttachment(index);
          if (updated?.compressionResult === 'done') compressedCount += 1;
          if (updated?.compressionResult === 'failed') failedCount += 1;
        }
      } finally {
        this.attachmentCompressQueue = [];
      }
      this.attachmentCompressNote = describeAttachmentCompression(
        beforeBytes,
        getAttachmentsTotalBytes(this.attachments),
        { compressedCount, failedCount }
      );
      if (attachmentsExceedBillLimit(this.attachments)) {
        this.showAlertModal(
          'Unable to save bill',
          buildBillAttachmentLimitMessage(getAttachmentsTotalBytes(this.attachments))
        );
      }
    },
    removeBillAttachment(index) {
      if (this.compressingAttachments) return;
      this.attachmentCompressNote = '';
      if (this.attachments && this.attachments.length > index) {
        if (this.attachmentPreviewUrls[index]) {
          URL.revokeObjectURL(this.attachmentPreviewUrls[index]);
          delete this.attachmentPreviewUrls[index];
        }
        this.attachments.splice(index, 1);
        const newPreviewUrls = {};
        this.attachments.forEach((attachment, newIndex) => {
          if (attachment.file) {
            newPreviewUrls[newIndex] = URL.createObjectURL(attachment.file);
          }
        });
        this.attachmentPreviewUrls = newPreviewUrls;
      }
    },
    async viewBillAttachment(attachment) {
      if (!attachment || !attachment.filePath) return;
      try {
        const attachmentIndex = this.attachments.findIndex(a => a === attachment);
        const previewUrl = attachmentIndex >= 0 ? this.attachmentPreviewUrls[attachmentIndex] : null;
        if (previewUrl) {
          window.open(previewUrl, '_blank');
          return;
        }
        if (attachment.filePath.startsWith('data:')) {
          const base64Data = attachment.filePath.split(',')[1];
          const mimeType = attachment.mimeType || 'application/pdf';
          const blob = this.base64ToBlob(base64Data, mimeType);
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
          return;
        }
        const companyId = this.$route.params.id || this.$route.params.companyId;
        let filename = attachment.filePath.includes('/') ? attachment.filePath.split('/').pop() : attachment.filePath;
        const viewUrl = companyId
          ? `/vendor/documents/${filename}?companyId=${companyId}`
          : `/vendor/documents/${filename}`;
        const response = await ApiService.get(viewUrl, { responseType: 'blob' });
        const mimeType = response.headers['content-type'] || attachment.mimeType || 'application/pdf';
        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (error) {
        logger.error('Failed to view bill attachment:', error);
        this.errorMessage = 'Failed to view attachment';
        this.showErrorModal = true;
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    isImageFile(attachment) {
      if (!attachment) return false;
      const mimeType = attachment.mimeType || '';
      return mimeType.startsWith('image/');
    },
    base64ToBlob(base64, mimeType) {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    },
    resolvedCompanyId() {
      const r = this.$route && this.$route.params;
      return (r && (r.companyId || r.id)) || null;
    },
    escapeHtmlForPdf(s) {
      return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    getBillNotifyPaymentTermLabel() {
      const term = (this.paymentTermsList || []).find((t) => String(t.id) === String(this.paymentTerm));
      if (!term) return '';
      return String(term.name || term.description || (term.dueDays != null ? `Net ${term.dueDays}` : '') || '').trim();
    },
    /** Rows used for SMS/email and PDF (auto lines + manual account rows, same idea as save payload). */
    getBillNotifyCombinedAccountRows() {
      const auto = (this.visibleAccountRows || []).filter(
        (row) => row.accountId && row.amount && Number(row.amount) > 0
      );
      const manual = (this.accountRows || []).filter(
        (row) => row.accountId && row.amount && Number(row.amount) > 0
      );
      return [...auto, ...manual];
    },
    getBillNotifyAccountLinesForMessage() {
      return this.getBillNotifyCombinedAccountRows().map((row) => ({
        description: row.description || '',
        amount: Number(row.amount) || 0,
      }));
    },
    buildBillNotificationPdfHtml() {
      const companyName = this.escapeHtmlForPdf(
        this.$store.state.currentCompany?.name || this.$store.state.currentCompany?.companyName || ''
      );
      const vendorName = this.escapeHtmlForPdf(this.selectedVendor?.vendorName || '');
      const billNo = this.escapeHtmlForPdf(this.invNo || '');
      const billDate = this.escapeHtmlForPdf(this.convertDateForBackend(this.billDate) || '');
      const dueDate = this.escapeHtmlForPdf(this.convertDateForBackend(this.dueDate) || '');
      const deliveryDate = this.escapeHtmlForPdf(
        this.convertDateForBackend(this.deliveryDate || this.billDate) || ''
      );
      const comments = this.comments ? this.escapeHtmlForPdf(this.comments) : '';
      const rows = this.getBillNotifyCombinedAccountRows()
        .map((row) => {
          const desc = this.escapeHtmlForPdf(row.description || '');
          const amt = Number(row.amount) || 0;
          return `<tr><td>${desc}</td><td class="num">$${amt.toFixed(2)}</td></tr>`;
        })
        .join('');
      const total = Number(this.finalTotal) || 0;
      return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
  body{font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a;margin:24px;}
  h1{font-size:22px;margin:0 0 4px 0;color:#312e81;}
  .meta{color:#64748b;font-size:13px;margin-bottom:20px;}
  table.hdr td{padding:6px 12px 6px 0;vertical-align:top;font-size:13px;}
  table.data{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}
  table.data th,table.data td{border:1px solid #e2e8f0;padding:8px 10px;}
  table.data th{background:#eef2ff;text-align:left;font-size:12px;color:#4338ca;}
  table.data td.num{text-align:right;}
</style></head><body>
  <h1>Create bill</h1>
  <div class="meta">${companyName}</div>
  <table class="hdr" cellpadding="0" cellspacing="0">
    <tr><td><strong>Vendor</strong></td><td>${vendorName}</td></tr>
    <tr><td><strong>Bill #</strong></td><td>${billNo}</td></tr>
    <tr><td><strong>Bill date</strong></td><td>${billDate}</td></tr>
    <tr><td><strong>Due date</strong></td><td>${dueDate}</td></tr>
    <tr><td><strong>Service date</strong></td><td>${deliveryDate}</td></tr>
    <tr><td><strong>Total</strong></td><td>$${total.toFixed(2)}</td></tr>
    ${comments ? `<tr><td><strong>Comments</strong></td><td>${comments}</td></tr>` : ''}
  </table>
  <table class="data">
    <thead><tr><th>Description</th><th class="num">Amount</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="2">No lines</td></tr>'}</tbody>
  </table>
</body></html>`;
    },
    buildBillCompanyNotifyPayload(companyId, pdfOverride = null) {
      const companyName =
        this.$store.state.currentCompany?.name ||
        this.$store.state.currentCompany?.companyName ||
        '';
      const paymentTermLabel = this.getBillNotifyPaymentTermLabel();
      const payload = {
        companyId,
        companyName,
        vendorName: this.selectedVendor?.vendorName || '',
        vendorEmail: this.selectedVendor?.email || '',
        billNo: this.invNo,
        billDate: this.convertDateForBackend(this.billDate),
        dueDate: this.convertDateForBackend(this.dueDate),
        deliveryDate: this.convertDateForBackend(this.deliveryDate || this.billDate),
        paymentTermLabel,
        total: this.finalTotal,
        contactName: this.contactName,
        phone: this.phone,
        comments: this.comments,
        accountLines: this.getBillNotifyAccountLinesForMessage(),
      };
      if (pdfOverride?.pdfBase64 && pdfOverride?.pdfFileName) {
        payload.pdfBase64 = pdfOverride.pdfBase64;
        payload.pdfFileName = pdfOverride.pdfFileName;
      }
      return payload;
    },
    async sendCompanyBillNotificationsAfterSave(companyId, billId, pdfOverride = null) {
      try {
        const cid = companyId || this.resolvedCompanyId();
        if (!cid || !billId) return;
        const payload = this.buildBillCompanyNotifyPayload(cid, pdfOverride);
        await notifyCompanyBillSaved(payload);
      } catch (e) {
        logger.warn('sendCompanyBillNotificationsAfterSave', e);
      }
    },
    async runCompanyBillNotificationsAfterSave() {
      const companyId = this.resolvedCompanyId();
      const billId = this.savedBillId;
      if (!companyId || !billId) return;
      let pdfAttachment = null;
      try {
        const htmlContent = this.buildBillNotificationPdfHtml();
        const pdfRes = await ApiService.post(`/vendor/enter-bill/${billId}/generate-notification-pdf`, {
          companyId,
          htmlContent,
        });
        const d = pdfRes.data?.data || pdfRes.data;
        if (d?.pdfBase64 && d?.pdfFileName) {
          pdfAttachment = { pdfBase64: d.pdfBase64, pdfFileName: d.pdfFileName };
        }
      } catch (pdfErr) {
        logger.warn('Bill notification PDF:', pdfErr);
      }
      await this.sendCompanyBillNotificationsAfterSave(companyId, billId, pdfAttachment);
    },
    snapshotDraftForHistory() {
      return {
        billId: this.editingBillId,
        billNo: this.invNo || '',
        vendorId: this.vendorId || '',
        vendorName: this.selectedVendor?.vendorName || this.selectedVendor?.name || this.$route.query.vendorName || '',
        contactName: this.contactName || '',
        phone: this.phone || '',
        paymentTerm: this.paymentTerm || '',
        paymentTermLabel: this.paymentTermAuditLabel(),
        date: this.billDate || '',
        dueDate: this.dueDate || '',
        total: this.finalTotal,
        status: 'Draft',
        consultantHours: (this.consultantHoursRows || [])
          .filter((row) => row.id)
          .map((row) => ({
            consultantId: row.id,
            hours: Number(row.hours) || 0,
            hourlyRate: Number(row.hourlyRate) || 0,
            description: row.description || '',
          })),
        items: (this.productRows || [])
          .filter((row) => row.itemId)
          .map((row) => ({
            itemId: row.itemId,
            description: row.description || '',
            quantity: Number(row.quantity) || 0,
            unitPrice: Number(row.unitPrice) || 0,
          })),
        attachments: (this.attachments || []).map((att) => att.fileName || att.name).filter(Boolean),
      };
    },
    buildCapturedAdjustments(before, after) {
      const rows = [];
      const push = (field, left, right) => {
        const a = left == null ? '' : String(left);
        const b = right == null ? '' : String(right);
        if (a === b) return;
        rows.push({ field, before: a, after: b });
      };
      push('Bill no', before.billNo, after.billNo);
      push('Bill date', before.date, after.date);
      push('Due date', before.dueDate, after.dueDate);
      push('Payment terms', before.paymentTermLabel || before.paymentTerm, after.paymentTermLabel || after.paymentTerm);
      push('Contact', before.contactName, after.contactName);
      push('Phone', before.phone, after.phone);
      push('Amount', before.total, after.total);
      push('Consultant hours', JSON.stringify(before.consultantHours || []), JSON.stringify(after.consultantHours || []));
      push('Items', JSON.stringify(before.items || []), JSON.stringify(after.items || []));
      push('Attachments', (before.attachments || []).join(', '), (after.attachments || []).join(', '));
      return rows;
    },
    capturedMailSnapshot() {
      const review = this.capturedReview || {};
      return {
        messageId: this.$route.query.capturedMessageId || review.messageId || '',
        mailService: review.mailService || 'outlook',
        subject: review.subject || '',
        fromAddress: review.fromAddress || '',
        fromName: review.fromName || '',
        receivedDateTime: review.receivedDateTime || null,
        bodyPreview: review.bodyPreview || '',
        summary: review.summary || '',
        hasAttachments: !!review.hasAttachments,
        attachments: Array.isArray(review.attachments) ? review.attachments : [],
      };
    },
    async completeCapturedReviewHistory(savedBillId) {
      const messageId = this.$route.query.capturedMessageId;
      const companyId = this.$route.params.id;
      if (!messageId || !companyId || !this.editingBillId) return;
      const posted = this.snapshotDraftForHistory();
      posted.status = 'Open';
      posted.billId = savedBillId || this.editingBillId;
      const before = this.originalDraftSnapshot || {};
      try {
        const url = `${process.env.VUE_APP_API_URL}/outlook/bill-mail/complete`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId,
            messageId,
            draftBillId: this.editingBillId,
            billId: savedBillId || this.editingBillId,
            mailService: this.capturedReview?.mailService || 'outlook',
            draftSnapshot: before,
            postedSnapshot: posted,
            adjustments: this.buildCapturedAdjustments(before, posted),
            capturedMail: this.capturedMailSnapshot(),
          }),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || 'Failed to close captured mail');
        }
      } catch (error) {
        logger.warn('Captured mail was posted, but the done history was not saved:', error);
      }
    },
    async saveBill() {
      if (attachmentsExceedBillLimit(this.attachments)) {
        const limitMessage = buildBillAttachmentLimitMessage(getAttachmentsTotalBytes(this.attachments));
        const error = new Error(limitMessage);
        error.response = {
          status: 413,
          data: { error: limitMessage, code: 'ATTACHMENTS_TOO_LARGE' }
        };
        throw error;
      }
      if (this.editingBillId && (this.$route.query.fromCaptured || this.capturedReview)) {
        this.finalizeCapturedBillAuditComment();
      }
      // Prepare payload according to backend structure
      // Each account record now represents an individual Product/Service item or Consultant timesheet
      // with its own individual amount (not grouped totals)
      // Service Date automatically uses Bill Date
      const deliveryDateToUse = this.deliveryDate || this.billDate;
      const payload = {
        vendorId: this.vendorId,
        contactName: this.contactName,
        phone: this.phone,
        paymentTerm: this.paymentTerm,
        date: this.convertDateForBackend(this.billDate),
        dueDate: this.convertDateForBackend(this.dueDate),
        deliveryDate: this.convertDateForBackend(deliveryDateToUse),
        billNo: this.invNo,
        total: this.finalTotal, // Include total amount
        comments: this.comments,
        attachments: (this.attachments || []).map(att => ({
          fileName: att.fileName,
          fileSize: att.fileSize,
          filePath: att.filePath,
          mimeType: att.mimeType,
          source: 'bill'
        })),
        accountRecords: [
          // Auto-populated account records from consultants/product-service
          ...this.visibleAccountRows
            .filter(row => row.accountId && row.amount && row.amount > 0)
            .map(row => ({
              accountId: row.accountId,
              description: row.description,
              amount: Number(row.amount) || 0,
              isManualRecord: false // Auto-generated from consultant/item records
            })),
          // Manual account records
          ...this.accountRows
            .filter(row => row.accountId && row.amount && row.amount > 0)
            .map(row => ({
              accountId: row.accountId,
              description: row.description,
              amount: Number(row.amount) || 0,
              isManualRecord: true // Manually added custom account record
            }))
        ],
        // Send product/service item records so backend can update their status to 'Bill Created'
        itemRecords: this.productRows
          .filter(row => row.itemId && row.itemType && row.quantity && row.unitPrice)
          .map(row => ({
            itemId: row.itemId,
            itemType: row.itemType,
            description: row.description,
            quantity: row.quantity,
            unitPrice: row.unitPrice
          })),
        // Send consultant timesheet records so backend can update their status to 'Bill Created'
        // Include both "Consultant Timesheet Records" section and "Itemize by Consultant Hours" section
        consultantTimesheetRecords: this.isConsultantIndustry
          ? [
              // From "Consultant Timesheet Records" section
              ...this.consultantTimesheets
                .filter(consultant => consultant.timesheetId)
                .map(consultant => ({
                  consultantId: consultant.id,
                  timesheetId: consultant.timesheetId,
                  hours: consultant.hours,
                  hourlyRate: consultant.hourlyRate,
                  description: consultant.description,
                  attachment: consultant.attachment
                })),
              // From "Itemize by Consultant Hours" section
              ...this.consultantHoursRows
                .filter(consultant => consultant.id && consultant.timesheetId)
                .map(consultant => ({
                  consultantId: consultant.id,
                  timesheetId: consultant.timesheetId,
                  hours: consultant.hours,
                  hourlyRate: consultant.hourlyRate,
                  description: consultant.description || '',
                  attachment: consultant.timesheetData?.attachments?.[0] || null
                }))
            ]
          : [],
        absorbDraftBillIds: String(this.$route.query.relatedBillIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean),
      };
      
      // Debug: Log the payload being sent
      logger.log('💾 Saving bill with payload:');
      logger.log('  - Total Amount:', payload.total);
      logger.log('  - Account Total:', this.accountTotal);
      logger.log('  - Individual Account Records:', payload.accountRecords.length);
      logger.log('  - Item Records:', payload.itemRecords.length);
      logger.log('  - Consultant Records:', payload.consultantTimesheetRecords.length);
      logger.log('  - Individual Account Details:', payload.accountRecords);
      logger.log('  - Full Payload:', payload);
      
      if (this.editingBillId) {
        payload.status = 'Open';
        if (this.$route.query.fromVendorSubmission) {
          payload.isVendorSubmitted = true;
        }
        const response = await ApiService.put(`/vendor/enter-bill/${this.editingBillId}`, payload);
        if (response && response.data && response.data.id) {
          this.savedBillId = response.data.id;
        }
        if (this.$route.query.fromCaptured || this.capturedReview) {
          await this.completeCapturedReviewHistory(this.savedBillId || this.editingBillId);
        }
        return response;
      }

      const response = await ApiService.post('/vendor/enter-bill', payload);
      
      // Store the saved bill ID for journal entry reference
      if (response && response.data && response.data.id) {
        this.savedBillId = response.data.id;
        logger.log('✅ Bill saved successfully with ID:', response.data.id);
      }
      
      return response;
    },
    convertDateForBackend(dateString) {
      if (!dateString) return '';
      const date = this.parseDateFromDatePicker(dateString);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    async resetForm(skipSuccessModal = false) {
      // Increment serial number for next bill
      this.serialNo++;
      
      // Reset all form fields
      this.vendorId = '';
      this.contactName = '';
      this.phone = '';
      this.paymentTerm = '';
      this.previousPaymentTerm = '';
      this.billDate = this.formatDateForDatePicker(new Date());
      this.dueDate = '';
      // Service Date automatically uses Bill Date
      this.deliveryDate = this.billDate;
      this.invNo = '';
      this.accountRows = [{ accountId: '', description: '', amount: 0 }];
      this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
      this.comments = '';
      this.payFrom = '';
      this.payFromBalanceError = '';
      // Reset bill attachments
      this.attachments = [];
      Object.values(this.attachmentPreviewUrls || {}).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      this.attachmentPreviewUrls = {};
      // Reset consultant timesheet data
      this.showConsultantTimesheets = false;
      this.consultantTimesheets = [];
      this.consultantOptions = [];
      this.allConsultantOptions = [];
      this.consultantsLoading = false;
      this.consultantsError = '';
      // Reset consultant hours rows (new key forces dropdown remount)
      this.consultantHoursRows = [this.createEmptyConsultantHoursRow()];
      this.consultantHoursRowsKey = (this.consultantHoursRowsKey || 0) + 1;
      // Reset expense account mappings
      this.expenseAccountMappings = [];
      // Reset product service list
      this.productServiceList = [];
      // Reset vendor bills list
      this.vendorBills = [];
      this.draftLinkedTimesheetIds = [];
      this.draftLinkedTimesheetIdsVendorId = null;
      this.draftLinkedTimesheetIdsExcludeBillId = null;
      // Reset saved / edit bill IDs so the next save creates a new bill
      this.savedBillId = null;
      this.editingBillId = null;
      this.journalAutoCreateActive = false;
      this.applyDefaultItemizeTab();
      this.tabPanelEnter = this.activeTab;

      if (!skipSuccessModal) {
        this.showSuccessModal('Ready', 'Form reset successfully! Ready to create a new bill.', 'OK');
      }
    },
    handleCancel() {
      // Navigate back to the previous view
      this.$router.back();
    },
    createCreateBillProgressorSteps() {
      return [
        { key: 'bill', label: 'Save bill', status: 'pending', detail: '' },
        { key: 'journal', label: 'Create journal entries', status: 'pending', detail: '' }
      ];
    },
    beginCreateBillPipeline(pipeline) {
      this.createBillPipeline = pipeline;
      this.createBillProgressorSteps = this.createCreateBillProgressorSteps();
      this.createBillProgressorFootMessage = '';
      this.createBillProgressorFootTone = 'neutral';
      this.createBillProgressorOkEnabled = false;
      this.createBillProgressorShow = true;
    },
    abortCreateBillPipeline() {
      this.createBillProgressorShow = false;
      this.createBillProgressorOkEnabled = false;
      this.createBillProgressorFootMessage = '';
      this.createBillProgressorSteps = [];
      this.createBillPipeline = null;
      this.journalAutoCreateActive = false;
    },
    updateCreateBillProgressorStep(key, patch) {
      const step = this.createBillProgressorSteps.find((s) => s.key === key);
      if (step) {
        Object.assign(step, patch);
      }
    },
    setCreateBillPipelineComplete() {
      const p = this.createBillPipeline;
      if (!p) return;
      if (p.journalFailed) {
        this.createBillProgressorFootTone = 'warn';
        this.createBillProgressorFootMessage =
          'Bill is saved; journal entries could not be posted. Click OK to continue.';
      } else if (p.journalSkipped) {
        this.createBillProgressorFootTone = 'warn';
        this.createBillProgressorFootMessage =
          'Bill is saved (journal was skipped). Click OK to continue.';
      } else {
        this.createBillProgressorFootTone = 'success';
        this.createBillProgressorFootMessage = 'All steps completed successfully. Click OK to finish.';
      }
      this.createBillProgressorOkEnabled = true;

      // Clear the form as soon as save pipeline finishes (Save and New),
      // so Create Bill is ready for another entry without waiting for OK.
      if (p.pendingAction === 'saveAndNew' && !p.formAlreadyReset) {
        p.formAlreadyReset = true;
        this.resetForm(true).catch((err) => {
          logger.error('Failed to reset Create Bill form after save and new:', err);
        });
      }
    },
    closeJournalEntryModal() {
      this.journalAutoCreateActive = false;
    },
    handleJournalEntrySaved(journalEntry) {
      if (this.createBillPipeline) {
        this.journalAutoCreateActive = false;
        this.createBillPipeline.journalEntry = journalEntry;
        const d = journalEntry?.entryNo ? `Entry number: ${journalEntry.entryNo}` : '';
        this.updateCreateBillProgressorStep('journal', { status: 'done', detail: d });
        this.setCreateBillPipelineComplete();
        return;
      }
      this.closeJournalEntryModal();
    },
    handleJournalEntryAutoSkipped({ reason } = {}) {
      const r = (reason || '').trim();
      if (this.createBillPipeline) {
        this.journalAutoCreateActive = false;
        this.createBillPipeline.journalEntry = null;
        this.createBillPipeline.journalSkipped = true;
        this.createBillPipeline.journalFailed = false;
        this.updateCreateBillProgressorStep('journal', {
          status: 'skipped',
          detail: r || 'Journal was not posted.'
        });
        this.setCreateBillPipelineComplete();
      }
    },
    handleJournalEntryAutoFailed({ error } = {}) {
      const r = (error || '').toString().trim();
      if (this.createBillPipeline) {
        this.journalAutoCreateActive = false;
        this.createBillPipeline.journalEntry = null;
        this.createBillPipeline.journalSkipped = false;
        this.createBillPipeline.journalFailed = true;
        this.createBillPipeline.journalFailureDetail = r;
        this.updateCreateBillProgressorStep('journal', {
          status: 'error',
          detail: r || 'Could not create journal entries.'
        });
        this.setCreateBillPipelineComplete();
      }
    },
    createBillJournalOutcomeLine(p) {
      if (p.journalFailed) {
        const d = (p.journalFailureDetail || '').trim();
        return d
          ? `Bill journal could not be created: ${d}`
          : 'Bill journal could not be created.';
      }
      if (p.journalSkipped) {
        return 'Bill journal was not created automatically.';
      }
      const entryNo = p.journalEntry?.entryNo || '';
      if (entryNo) {
        return `Bill journal created. Entry number: ${entryNo}.`;
      }
      return 'Bill journal created successfully.';
    },
    async onCreateBillProgressorOk() {
      if (!this.createBillProgressorOkEnabled || !this.createBillPipeline) return;
      const p = { ...this.createBillPipeline };
      this.createBillProgressorShow = false;
      this.createBillProgressorOkEnabled = false;
      this.createBillProgressorFootMessage = '';
      this.createBillProgressorSteps = [];
      this.createBillPipeline = null;

      await this.finalizeCreateBillPipelineOk(p);
    },
    async finalizeCreateBillPipelineOk(p) {
      const inv = p.savedInvNo || this.invNo;
      const journalLine = this.createBillJournalOutcomeLine(p);

      if (p.pendingAction === 'saveAndNew') {
        if (!p.formAlreadyReset) {
          await this.resetForm(true);
        }
        this.showSuccessModal('Success', `Bill ${inv} saved. ${journalLine} Form reset for new bill.`, 'OK');
        return;
      }

      if (p.pendingAction === 'saveAndCloseToPayBill') {
        await this.resetForm(true);
        this.showSuccessModal('Success', `Bill ${inv} saved. ${journalLine}`, 'OK');
        this.$router.push(`/company/${this.$route.params.id}/dashboard/vendors/pay-bill`);
        return;
      }

      if (p.pendingAction === 'saveAndClose') {
        await this.resetForm(true);
        this.showSuccessModal('Success', `Bill ${inv} saved. ${journalLine}`, 'OK');
        this.$router.push(`/company/${this.$route.params.id}/dashboard/vendors`);
      }
    },
    validateRequiredFields() {
      const errors = [];
      
      // Check if vendor is selected
      if (!this.vendorId || this.vendorId === '') {
        errors.push('• Vendor Name is required');
      }
      
      // Check if bill date is selected
      if (!this.billDate || this.billDate === '') {
        errors.push('• Bill Date is required');
      }
      
      // Service Date automatically uses Bill Date, so no validation needed
      
      return errors;
    },
    showValidationError(errors) {
      this.errorModalTitle = 'Please Complete Required Fields';
      this.errorModalMessage = `Please fix the following issues before saving:\n\n${errors.join('\n')}`;
      this.errorModalButtonText = 'OK';
      this.showErrorModal = true;
    },
    onVendorCreated(newVendor) {
      this.showVendorCreationDialog = false;
      this.refreshVendorDataAfterDialog(newVendor);
    },
    async refreshVendorDataAfterDialog(newVendor) {
      // Ensure both vendor list and payment terms are refreshed so new vendor defaults are available
      await Promise.all([
        this.fetchVendorList(),
        this.fetchPaymentTerms()
      ]);

      if (newVendor) {
        const createdVendorId = String(newVendor.id || newVendor.vendorId || '');

        if (createdVendorId) {
          const createdVendor = this.vendorList.find(v => String(v.id) === createdVendorId);

          this.vendorId = createdVendor ? createdVendorId : '';

          // If the newly created vendor has a default payment term, apply it immediately
          const newVendorPaymentTermId = newVendor.paymentTermId || newVendor.paymentTerm?.id;
          if (newVendorPaymentTermId) {
            this.paymentTerm = String(newVendorPaymentTermId);
            this.previousPaymentTerm = String(newVendorPaymentTermId);
          }

          await this.populateVendorDefaults(createdVendorId);

          return;
        }
      }

      // Fallback: reset selections when vendor creation data is unavailable
      this.vendorId = '';
    },
    async onConsultantCreated(newConsultant) {
      this.showConsultantCreationDialog = false;
      const index = this.currentConsultantHoursIndex;
      this.currentConsultantHoursIndex = null;
      if (!newConsultant || !newConsultant.id) return;
      const exists = this.allCompanyConsultants.some(c => String(c.id) === String(newConsultant.id));
      if (!exists) {
        this.allCompanyConsultants.push(newConsultant);
      }
      if (index !== null && this.consultantHoursRows[index]) {
        const row = this.consultantHoursRows[index];
        row.id = newConsultant.id;
        row.description = newConsultant.consultantName || newConsultant.name || row.description;
        row.hourlyRate = Number(newConsultant.purchasePrice) || row.hourlyRate || 0;
        await this.fetchConsultantHoursTimesheets(index, newConsultant.id);
        
        // Refresh expense accounts after creating consultant to ensure account mappings are updated
        if (this.vendorId) {
          await this.fetchExpenseAccounts(this.vendorId);
          // Ensure Vue reactivity updates after fetching expense accounts
          await this.$nextTick();
        }
      }
      await Promise.all([
        this.fetchVendorList(),
        this.fetchPaymentTerms()
      ]);
    }
  },
  watch: {
    async vendorId(val) {
      if (val === '__create_new__') {
        // Open inline Vendor Creation Dialog
        this.showVendorCreationDialog = true;
        // Reset the select value until vendor is created
        this.vendorId = '';
        return;
      }
      const selected = this.vendorList.find(v => String(v.id) === String(val));
      if (selected) {
        // Check if vendor changed (not initial load)
        const vendorChanged = this.previousVendorId !== null && 
          String(this.previousVendorId) !== String(val);
        const preserveDraftHeader = !!(this.loadingExistingBill || this.editingBillId) && !vendorChanged;

        if (this.$route.query.consultantId && !vendorChanged) {
          this.routeConsultantPrefillApplied = false;
        }
        
        // Initialize rows when vendor is selected
        if (this.productRows.length === 0) {
          this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
        }
        if (this.accountRows.length === 0) {
          this.accountRows = [{ accountId: '', description: '', amount: 0 }];
        }
        
        // If vendor changed, reset Description, Unit Price, and Hourly Rate fields
        if (vendorChanged) {
          // Reset Product/Service rows: Description, Unit Price, and clear selections
          this.productRows.forEach(row => {
            row.description = '';
            row.unitPrice = 0;
            row.itemId = '';
            row.itemType = '';
            row.selectedItem = '';
            // Keep quantity as user might want to keep it
          });
          
          // Reset Consultant Hours rows: Description, Hourly Rate, and Hours
          this.consultantHoursRows.forEach(row => {
            row.description = '';
            row.hourlyRate = 0;
            row.hours = 0;
            row.id = '';
            row.timesheetId = null;
            row.timesheetData = null;
            row.availableTimesheets = [];
            row.selectedTimesheetId = '';
          });
        }
        
        // Itemize by Consultant Hours uses allCompanyConsultants — load it immediately for this
        // vendor, in parallel with slower calls (timesheets loop, etc.). Clear stale options first
        // so users cannot pick consultants from the previous vendor while requests are in flight.
        if (!preserveDraftHeader) {
          this.allCompanyConsultants = [];
        }
        const consultantHoursListPromise = this.fetchAllConsultantsForCompany(val);
        
        // Preserve existing product rows selections before fetching new items
        // Deep copy to preserve all row data including selectedItem, description, quantity, unitPrice
        const preservedProductRows = this.productRows
          .filter(row => row.itemId || row.selectedItem || (row.description && row.quantity > 0))
          .map(row => JSON.parse(JSON.stringify(row))); // Deep clone to preserve all data
        
        // Update product/service items filtered by selected vendor
        await this.fetchProductServiceItems(val);

        // Fetch complete vendor details to apply defaults (contacts, payment term, etc.)
        await this.populateVendorDefaults(val, { overwriteHeader: !preserveDraftHeader });
        
        // Restore preserved product rows after fetching new items
        if (preservedProductRows.length > 0 && !vendorChanged) {
          // Only restore if vendor didn't change (to preserve previous logic for auto-selection scenarios)
          // Check if productRows was reset to single empty row
          const wasReset = this.productRows.length === 1 && 
            !this.productRows[0].itemId && 
            !this.productRows[0].selectedItem;
          
          if (wasReset) {
            // Replace the empty row with preserved rows
            this.productRows = preservedProductRows;
            // Add an empty row at the end for new entries
            this.productRows.push({ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 });
          } else {
            // Merge preserved rows with existing (avoid duplicates)
            preservedProductRows.forEach(preservedRow => {
              const exists = this.productRows.some(row => 
                row.itemId === preservedRow.itemId && 
                row.itemType === preservedRow.itemType &&
                String(row.itemId || '') === String(preservedRow.itemId || '')
              );
              if (!exists) {
                // Add preserved row
                this.productRows.push(preservedRow);
              } else {
                // Update existing row to preserve user's selection (keep user's data)
                const existingRow = this.productRows.find(row => 
                  row.itemId === preservedRow.itemId && 
                  row.itemType === preservedRow.itemType
                );
                if (existingRow) {
                  // Preserve user's manual changes (but reset description and unitPrice if vendor changed)
                  Object.assign(existingRow, {
                    selectedItem: preservedRow.selectedItem,
                    itemId: preservedRow.itemId,
                    itemType: preservedRow.itemType,
                    description: vendorChanged ? '' : (preservedRow.description || existingRow.description),
                    quantity: preservedRow.quantity || existingRow.quantity,
                    unitPrice: vendorChanged ? 0 : (preservedRow.unitPrice || existingRow.unitPrice)
                  });
                }
              }
            });
          }
        }
        
        // Update previous vendor ID
        this.previousVendorId = String(val);
        this.draftLinkedTimesheetIds = [];
        this.draftLinkedTimesheetIdsVendorId = null;
        this.draftLinkedTimesheetIdsExcludeBillId = null;
        this.contactList = selected.contacts || this.contactList || [];
        this.applyDefaultVendorContact({ force: vendorChanged });
        
        // Fetch vendor's previous bills for bill number increment
        try {
          const bills = await ApiService.getVendorBills(val);
          this.vendorBills = Array.isArray(bills) ? bills : [];
        } catch (err) {
          logger.error('Error fetching vendor bills:', err);
          this.vendorBills = [];
        }
        
        if (!preserveDraftHeader) {
          // Auto-select vendor's preferred payment term
          if (selected.paymentTermId) {
            const matchingPaymentTerm = this.paymentTermsList.find(term => 
              String(term.id) === String(selected.paymentTermId)
            );
            if (matchingPaymentTerm) {
              this.paymentTerm = selected.paymentTermId;
              this.previousPaymentTerm = selected.paymentTermId;
            } else {
              logger.warn(`Vendor's preferred payment term ID ${selected.paymentTermId} not found in payment terms list`);
            }
          } else {
            this.paymentTerm = '';
            this.previousPaymentTerm = '';
          }
        } else if (!this.paymentTerm) {
          // Draft open: keep header, but still fill missing payment term from vendor
          await this.applyVendorPreferredPaymentTerm(val);
        }
        
        // Fetch expense accounts for this vendor (products, services, consultants)
        await this.fetchExpenseAccounts(val);
        
        // Fetch consultant timesheets for this vendor
        await this.fetchConsultantsForVendor(val);
        
        await consultantHoursListPromise;
        await this.applyRouteConsultantPrefill();
      } else {
        // No vendor selected - clear vendor-specific data but keep tables visible
        // Reset fields when vendor is cleared
        const vendorCleared = this.previousVendorId !== null && val === '';
        if (vendorCleared) {
          // Reset Product/Service rows: Description, Unit Price, and clear selections
          this.productRows.forEach(row => {
            row.description = '';
            row.unitPrice = 0;
            row.itemId = '';
            row.itemType = '';
            row.selectedItem = '';
            // Keep quantity as user might want to keep it
          });
          
          // Reset Consultant Hours rows: Description, Hourly Rate, and Hours
          this.consultantHoursRows.forEach(row => {
            row.description = '';
            row.hourlyRate = 0;
            row.hours = 0;
            row.id = '';
            row.timesheetId = null;
            row.timesheetData = null;
            row.availableTimesheets = [];
            row.selectedTimesheetId = '';
          });
        }
        
        await this.fetchProductServiceItems(); // Fetch all items when no vendor selected
        this.contactList = [];
        this.contactName = '';
        this.phone = '';
        this.vendorBills = [];
        this.draftLinkedTimesheetIds = [];
        this.draftLinkedTimesheetIdsVendorId = null;
        this.draftLinkedTimesheetIdsExcludeBillId = null;
        // Reset payment term when no vendor is selected
        this.paymentTerm = '';
        this.previousPaymentTerm = '';
        // Reset consultant data
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        this.consultantsError = '';
        // Clear expense account mappings when no vendor is selected
        this.expenseAccountMappings = [];
        // Fetch all consultants when no vendor is selected
        await this.fetchAllConsultantsForCompany();
        // Reset previous vendor ID
        this.previousVendorId = null;
        // Keep accountRows and productRows since tables are always visible
      }
      await this.generateInvNo();
    },
    contactName(val) {
      if (this.loadingExistingBill) return;
      const selectedContact = this.contactList.find(c => c.name === val);
      if (selectedContact) {
        this.phone = selectedContact.phone || '';
      } else {
        this.phone = '';
      }
    },
    async paymentTerm() {
      this.updateDueDate();
      await this.generateInvNo();
    },
    async billDate() {
      // Automatically sync Service Date with Bill Date
      this.updateDueDate();
      this.deliveryDate = this.billDate;
      await this.generateInvNo();
    },
    async serialNo() {
      await this.generateInvNo();
    },
    payFrom() {
      this.validatePayFromBalance();
    },
    accountRows: {
      handler() {
        this.validatePayFromBalance();
      },
      deep: true
    },
    productRows: {
      handler() {
        this.validatePayFromBalance();
        // Note: Removed auto-update of account rows to prevent overwriting manual entries
        // Account rows should be manually managed by the user
      },
      deep: true
    },
    consultantTimesheets: {
      handler() {
        this.validatePayFromBalance();
        // Note: Removed auto-update of account rows to prevent overwriting manual entries
        // Account rows should be manually managed by the user
      },
      deep: true
    },
  },
  beforeUnmount() {
    this.closeAccountInfo();
    Object.values(this.attachmentPreviewUrls || {}).forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    this.attachmentPreviewUrls = {};
  },
};
</script>
